// NewEditor.jsx
import React, { useState, useEffect } from "react";
import ReactFlow, { Background, Controls, ConnectionLineType, MiniMap } from "reactflow";
import "reactflow/dist/style.css";
import { useLocation } from "react-router-dom";
import { doc, onSnapshot, updateDoc, collection, getDocs, getDoc, addDoc, arrayUnion } from "firebase/firestore";
import { db } from "../firebase"; // Your firebase config file
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const NewEditor = () => {
  // Destructure sessionId, fileName, and userJourneys (if available) from router state.
  const location = useLocation();
  const { sessionId, fileName, userJourneys: journeysFromState } = location.state;

  // Local state for session data loaded from Firestore.
  const [session, setSession] = useState(null);
  // Other state variables
  const [outcomeTab, setOutcomeTab] = useState("Outcome");
  const [selectedNode, setSelectedNode] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isRecommendationLoading, setIsRecommendationLoading] = useState(false);
  const [isBoundingBoxLoading, setIsBoundingBoxLoading] = useState(false);
  const [isNavigationPopupOpen, setIsNavigationPopupOpen] = useState(false);
  const [selectedJourneyNavigation, setSelectedJourneyNavigation] = useState(null);
  const [isProcessingImage, setIsProcessingImage] = useState(false);
  const [isProcessingRecommendation, setIsProcessingRecommendation] = useState(false);
  const [isProcessingBoundingBox, setIsProcessingBoundingBox] = useState(false);
  const [isExportPDFLoading, setIsExportPDFLoading] = useState(false);
  const [journeyFlowData, setJourneyFlowData] = useState(null);
  const [feedback, setFeedback] = useState({
    hallucination: selectedNode?.hallucination_feedback || "",
    doubtfulness: selectedNode?.doubtfulness_feedback || "",
  });
  const [isRegulatoryPreventionsLoading, setIsRegulatoryPreventionsLoading] = useState(false);
  const [isAddingToDBLoading, setIsAddingToDBLoading] = useState(false);
  const [addToDBStatus, setAddToDBStatus] = useState(null);

  const [points, setPoints] = useState([]);
  const [feedbackText, setFeedbackText] = useState("");

  // Add new state for XAI popup
  const [isXAIPopupOpen, setIsXAIPopupOpen] = useState(false);
  const [selectedXAIPoint, setSelectedXAIPoint] = useState(null);

  // Add new state for laws management
  const [isLawsPopupOpen, setIsLawsPopupOpen] = useState(false);
  const [selectedPointForLaws, setSelectedPointForLaws] = useState(null);
  const [lawsData, setLawsData] = useState([]);
  const [selectedLaws, setSelectedLaws] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lawsFilter, setLawsFilter] = useState('');
  const [isAddingLaw, setIsAddingLaw] = useState(false);
  const [selectedLaw, setSelectedLaw] = useState('');
  const [selectedClause, setSelectedClause] = useState('');
  const [selectedLink, setSelectedLink] = useState('');

  // Modify state variables to include both laws and clauses
  const [lawsList, setLawsList] = useState([]);
  const [selectedLawId, setSelectedLawId] = useState('');
  const [clausesList, setClausesList] = useState([]);

  // Add new state for session name
  const [sessionName, setSessionName] = useState("");

  // Add new state for selected laws to remove
  const [lawsToRemove, setLawsToRemove] = useState([]);

  // Add state for loading and XAI details
  const [isXAILoading, setIsXAILoading] = useState(false);
  const [xaiDetails, setXAIDetails] = useState(null);

  // Add new state for Path B agents
  const [pathBAgents, setPathBAgents] = useState([]);
  const [activeAgents, setActiveAgents] = useState([]);

  // Add state for dark patterns
  const [darkPatterns, setDarkPatterns] = useState([]);
  const [isDarkPatternsLoading, setIsDarkPatternsLoading] = useState(false);

  // Add these new state variables near the top with other state declarations
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filterText, setFilterText] = useState('');
  const [isFetchingLaws, setIsFetchingLaws] = useState(false);

  // Add state for recommendations popup
  const [isRecommendationsPopupOpen, setIsRecommendationsPopupOpen] = useState(false);
  const [selectedPointForRecommendations, setSelectedPointForRecommendations] = useState(null);
  const [isGeneratingFairPattern, setIsGeneratingFairPattern] = useState(false);
  const [generatedFairPatternUrl, setGeneratedFairPatternUrl] = useState(null);
  const [isRepresentativePopupOpen, setIsRepresentativePopupOpen] = useState(false);
  const [isGeneratingRepresentative, setIsGeneratingRepresentative] = useState(false);

  // Add new state for image navigation and view mode
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [allImages, setAllImages] = useState([]);
  const [currentJourneyIndex, setCurrentJourneyIndex] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [viewMode, setViewMode] = useState('flow'); // 'flow' or 'split'
  const [activeTab, setActiveTab] = useState('all-screens'); // 'all-screens' or 'detailed-review'
  const [isPropertiesPopupOpen, setIsPropertiesPopupOpen] = useState(false);

  // Add state for edit mode
  const [editingPointIndex, setEditingPointIndex] = useState(null);
  const [tempEditData, setTempEditData] = useState({});

  // Add state for resizable split
  const [leftWidth, setLeftWidth] = useState(60); // percentage
  const [isDraggingDivider, setIsDraggingDivider] = useState(false);

  // Add these constants for jurisdiction priority
  const JURISDICTION_PRIORITY = {
    'EU': 1,
    'US Federal': 2,
    'US State': 3
  };

  // Add this function to collect all images from all journeys with chronological sorting
  const collectAllImages = (sessionData) => {
    if (!sessionData || !sessionData.userJourneys) {
      return [];
    }

    // First sort journeys by createdAt timestamp
    const sortedJourneys = [...sessionData.userJourneys].sort((a, b) => {
      const timeA = a.createdAt ? new Date(a.createdAt.seconds * 1000 + (a.createdAt.nanoseconds || 0) / 1000000) : new Date(0);
      const timeB = b.createdAt ? new Date(b.createdAt.seconds * 1000 + (b.createdAt.nanoseconds || 0) / 1000000) : new Date(0);
      return timeA - timeB;
    });

    const images = [];
    sortedJourneys.forEach((journey, journeyIndex) => {
      if (journey.images) {
        // Sort images within each journey by createdAt timestamp
        const sortedImages = [...journey.images].sort((a, b) => {
          const timeA = a.createdAt ? new Date(a.createdAt.seconds * 1000 + (a.createdAt.nanoseconds || 0) / 1000000) : new Date(0);
          const timeB = b.createdAt ? new Date(b.createdAt.seconds * 1000 + (b.createdAt.nanoseconds || 0) / 1000000) : new Date(0);
          return timeA - timeB;
        });

        sortedImages.forEach((image, imageIndex) => {
          images.push({
            ...image,
            journeyIndex,
            imageIndex,
            journeyName: journey.journeyName,
            journeyCreatedAt: journey.createdAt,
            imageCreatedAt: image.createdAt
          });
        });
      }
    });

    return images;
  };

  // Add this function to fetch laws with rankings
  const fetchLawsWithRanking = async () => {
    setLoading(true);
    try {
      const Airtable = require('airtable');
      const base = new Airtable({ apiKey: process.env.REACT_APP_AIRTABLE_API_KEY }).base('apprwDgpLMtBvjfTQ');

      const records = await base('tbl9vTiOe4NaUtDZY').select({
        fields: ['Laws', 'Jurisdiction', 'Ranking']
      }).all();

      const laws = records.map(record => ({
        id: record.id,
        shortName: record.fields['Laws'],
        jurisdiction: record.fields['Jurisdiction'] || 'N/A',
        ranking: record.fields['Ranking'] || 999 // Use high number for unranked items
      }));

      // Sort laws by jurisdiction priority and ranking (lower rank first)
      laws.sort((a, b) => {
        // First sort by jurisdiction priority
        const priorityDiff = (JURISDICTION_PRIORITY[a.jurisdiction] || 999) -
          (JURISDICTION_PRIORITY[b.jurisdiction] || 999);

        if (priorityDiff !== 0) return priorityDiff;

        // Then sort by ranking (lower number first)
        return (a.ranking || 999) - (b.ranking || 999);
      });

      setLawsList(laws);
    } catch (error) {
      console.error('Error fetching laws:', error);
    } finally {
      setLoading(false);
    }
  };

  // Add function to fetch dark patterns from Airtable
  const fetchDarkPatterns = async () => {
    setIsDarkPatternsLoading(true);
    try {
      const Airtable = require('airtable');
      const base = new Airtable({ apiKey: process.env.REACT_APP_AIRTABLE_API_KEY }).base('apprwDgpLMtBvjfTQ');

      // First, let's get all records to see the structure
      const records = await base('tblXajPljs2LJPtfe').select().all();

      // console.log('Raw Airtable records:', records);
      // console.log('First record fields:', records[0]?.fields);

      const patterns = records.map(record => {
        // Try multiple ways to access the name field
        const nameValue =
          record.fields['fldwHbFBq8qyStXvp'] || // Field ID
          record.fields['Name'] || // Field name
          record.fields.Name || // Alternative access
          'Unknown Pattern';

        // console.log('Record ID:', record.id, 'Name value:', nameValue, 'All fields:', record.fields);

        return {
          id: record.id,
          name: nameValue
        };
      }).filter(pattern => pattern.name !== 'Unknown Pattern'); // Filter out unknown patterns

      // Sort alphabetically
      patterns.sort((a, b) => a.name.localeCompare(b.name));

      setDarkPatterns(patterns);
      // console.log('Processed dark patterns:', patterns);
    } catch (error) {
      console.error('Error fetching dark patterns:', error);
      console.error('Error details:', error.message);
    } finally {
      setIsDarkPatternsLoading(false);
    }
  };

  // Add this to your existing session loading useEffect
  useEffect(() => {
    if (!sessionId) return;
    const sessionDocRef = doc(db, "sessions", sessionId);

    const unsubscribe = onSnapshot(sessionDocRef, (docSnap) => {
      if (docSnap.exists()) {
        const sessionData = docSnap.data();
        console.log("Session data loaded from Firebase:", sessionData);

        // Set session name from the session data
        setSessionName(sessionData.sessionName || sessionId);

        // Ensure userJourneys is properly set
        if (!sessionData.userJourneys && journeysFromState) {
          sessionData.userJourneys = journeysFromState;
        }

        // Only set session if we have valid data structure
        if (sessionData.userJourneys && Array.isArray(sessionData.userJourneys)) {
          setSession(sessionData);

          // Collect all images and set the first one as selected
          const images = collectAllImages(sessionData);
          setAllImages(images);

          console.log("Session set with", sessionData.userJourneys.length, "journeys and", images.length, "total images");
        } else {
          console.warn("Session data doesn't have valid userJourneys array:", sessionData);
          // Set session anyway but log the issue
          setSession(sessionData);
          setAllImages([]);
        }
      } else {
        console.error("No session found with ID:", sessionId);
      }
    }, (error) => {
      console.error("Error listening to session:", error);
    });

    return () => unsubscribe();
  }, [sessionId, journeysFromState]);

  // Add effect to fetch clauses when law is selected
  useEffect(() => {
    if (selectedLawId) {
      fetchClausesForLaw(selectedLawId);
    } else {
      setClausesList([]);
    }
  }, [selectedLawId]);

  // Update selected node when current image index changes
  useEffect(() => {
    if (allImages.length > 0 && currentImageIndex >= 0 && currentImageIndex < allImages.length) {
      const selectedImage = allImages[currentImageIndex];
      handleNodeSelection(selectedImage);
    }
  }, [currentImageIndex, allImages]);

  // Modify the useEffect that handles selected node to load text feedback
  useEffect(() => {
    if (selectedNode) {
      // console.log("Loading feedback from selected node:", selectedNode.text_feedback);
      setFeedbackText(selectedNode.text_feedback || "");

      // Parse points from the response array
      const responsePoints = selectedNode.response || [];
      setPoints(responsePoints.map(point => ({
        text: point.text,
        type: point.type || point.kind || "outcome",
        laws: point.laws || [],
        dark_pattern: point.dark_pattern || null, // Include dark_pattern when loading points
        fairpatterns_url: point.fairpatterns_url || null,
        coordinates: point.coordinates || null, // Include coordinates when loading points
        priority: point.priority || 1, // Add priority with default value 1
        confirmedOutcome: point.confirmedOutcome || false, // Include confirmed outcome status
        recommendations: point.recommendations || [], // Include recommendations array
        fair_patterns: point.fair_patterns || null, // Include fair_patterns object
        fair_pattern_images: point.fair_pattern_images || [], // Include fair_pattern_images array
        fairpattern_image_generator: point.fairpattern_image_generator || null, // Include generated image URL
        selected: false
      })));

      // console.log("Loaded points:", responsePoints);
    } else {
      setFeedbackText("");
      setPoints([]);
    }
  }, [selectedNode]);

  // Add useEffect to update activeAgents when selectedNode changes
  useEffect(() => {
    if (selectedNode && selectedNode.agent_responses) {
      try {
        // Extract agent names from agent_responses object
        const agents = Object.keys(selectedNode.agent_responses);
        setActiveAgents(agents);
        // console.log("Active agents set:", agents);
      } catch (error) {
        console.error("Error extracting agent responses:", error);
        setActiveAgents([]);
      }
    } else {
      setActiveAgents([]);
    }
  }, [selectedNode]);

  // Add function to handle node selection
  // Add function to handle node selection with robust lookup
  const handleNodeSelection = async (nodeData) => {
    if (!nodeData) return;
    try {
      // console.log("Fetching fresh data for node:", nodeData.id || "no-id", nodeData.imageUrl || nodeData.image_url);
      
      // Get reference to all user journeys
      const journeysRef = collection(db, "sessions", sessionId, "userJourneys");
      const journeysSnapshot = await getDocs(journeysRef);
      
      let targetJourneyDoc = null;
      let targetImageDoc = null;

      // Optimization: Try to find by journeyName first if we have it
      let journeysToSearch = journeysSnapshot.docs;
      if (nodeData.journeyName) {
        const matchingJourneys = journeysSnapshot.docs.filter(doc => 
          doc.data().journeyName === nodeData.journeyName
        );
        if (matchingJourneys.length > 0) {
          journeysToSearch = matchingJourneys;
        }
      }

      // Find the image document
      for (const journeyDoc of journeysToSearch) {
        // Try to fetch by ID if available (fastest and most reliable)
        if (nodeData.id) {
          const imageRef = doc(journeyDoc.ref, "images", nodeData.id);
          const imageSnap = await getDoc(imageRef);
          if (imageSnap.exists()) {
            targetImageDoc = imageSnap;
            targetJourneyDoc = journeyDoc;
            break;
          }
        }

        // Fallback to URL matching or ID matching within the collection
        const imagesRef = collection(journeyDoc.ref, "images");
        const imagesSnapshot = await getDocs(imagesRef);

        const imageDoc = imagesSnapshot.docs.find(doc => {
          const data = doc.data();
          const targetUrl = nodeData.imageUrl || nodeData.image_url || nodeData.imageurl;
          return (
            data.imageUrl === targetUrl || 
            data.image_url === targetUrl || 
            data.imageurl === targetUrl || 
            (nodeData.id && doc.id === nodeData.id)
          );
        });

        if (imageDoc) {
          targetJourneyDoc = journeyDoc;
          targetImageDoc = imageDoc;
          break;
        }
      }

      // If not found in primary search, search other journeys only if we haven't already
      if (!targetImageDoc && journeysToSearch.length < journeysSnapshot.docs.length) {
        for (const journeyDoc of journeysSnapshot.docs) {
          if (journeysToSearch.includes(journeyDoc)) continue;
          
          const imagesRef = collection(journeyDoc.ref, "images");
          const imagesSnapshot = await getDocs(imagesRef);

          const imageDoc = imagesSnapshot.docs.find(doc => {
            const data = doc.data();
            const targetUrl = nodeData.imageUrl || nodeData.image_url || nodeData.imageurl;
            return (
              data.imageUrl === targetUrl || 
              data.image_url === targetUrl || 
              data.imageurl === targetUrl || 
              (nodeData.id && doc.id === nodeData.id)
            );
          });

          if (imageDoc) {
            targetJourneyDoc = journeyDoc;
            targetImageDoc = imageDoc;
            break;
          }
        }
      }

      if (!targetJourneyDoc || !targetImageDoc) {
        console.error("Image document not found in any journey for:", nodeData);
        // Still set selectedNode with what we have if we can't find it in sub-collection
        setSelectedNode(nodeData);
        return;
      }

      // Get fresh image data from Firebase
      const freshImageData = targetImageDoc.data();
      const parentJourney = targetJourneyDoc.data();

      // Construct updated node with fresh data
      const updatedNode = {
        ...nodeData,
        id: targetImageDoc.id, // Ensure ID is present
        pageName: freshImageData?.comprehensive_result?.categories?.webpage_category || freshImageData?.pageName || nodeData.pageName || "Unknown Page",
        journeyName: parentJourney?.journeyName || nodeData.journeyName || "Unknown",
        bounding_box_url: freshImageData?.bounding_box_url || null,
        hallucination: freshImageData?.hallucination || "No Hallucination Details Found",
        doubtful: freshImageData?.doubtful || "No Doubtfulness Details Found",
        outcome: freshImageData?.outcome || [],
        text_feedback: freshImageData?.text_feedback || "",
        feedback: freshImageData?.text_feedback || "",
        recommendation: freshImageData?.recommendation || null,
        regulatory_preventions: freshImageData?.regulatory_preventions || null,
        response: freshImageData?.response || [],
        agents: freshImageData?.agents || [],
        agent_responses: freshImageData?.agent_responses || null,
        comprehensive_result: freshImageData?.comprehensive_result || null,
        createdAt: freshImageData?.createdAt || freshImageData?.completedAt || nodeData.createdAt || nodeData.imageCreatedAt || null,
        image_hash: freshImageData?.image_hash || freshImageData?.hash || null,
      };

      console.log("Updated selected node with complete data:", updatedNode);
      setSelectedNode(updatedNode);

      // Update points array with ALL necessary fresh data from response
      const responseArray = freshImageData.response || [];
      const newPoints = responseArray.map(point => ({
        text: point.text,
        type: point.type || point.kind || "outcome",
        laws: point.laws || [],
        dark_pattern: point.dark_pattern || null,
        fairpatterns_url: point.fairpatterns_url || null,
        coordinates: point.coordinates || null,
        priority: point.priority || 1,
        confirmedOutcome: point.confirmedOutcome || false,
        recommendations: point.recommendations || [],
        fair_patterns: point.fair_patterns || null,
        fair_pattern_images: point.fair_pattern_images || [],
        fairpattern_image_generator: point.fairpattern_image_generator || null,
        selected: false
      }));
      setPoints(newPoints);

      // Update feedback text
      setFeedbackText(freshImageData?.text_feedback || "");

    } catch (error) {
      console.error("Error in handleNodeSelection:", error);
      toast.error("Failed to fetch fresh node data");
    }
  };


  // Add navigation functions
  const handlePreviousImage = () => {
    if (currentImageIndex > 0) {
      setCurrentImageIndex(currentImageIndex - 1);
      resetImageView();
    }
  };

  const handleNextImage = () => {
    if (currentImageIndex < allImages.length - 1) {
      setCurrentImageIndex(currentImageIndex + 1);
      resetImageView();
    }
  };

  // Add image zoom and pan functions
  const resetImageView = () => {
    setZoomLevel(1);
    setImagePosition({ x: 0, y: 0 });
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev * 1.2, 5));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev / 1.2, 0.1));
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX - imagePosition.x,
      y: e.clientY - imagePosition.y
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;

    setImagePosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Add function to go back to flow view
  const handleBackToFlow = () => {
    setActiveTab('all-screens');
    setViewMode('flow');
    setSelectedNode(null);
    setPoints([]);
    setFeedbackText("");
    resetImageView();
  };

  // Add functions for resizable divider
  const handleDividerMouseDown = (e) => {
    setIsDraggingDivider(true);
    e.preventDefault();
  };

  const handleDividerMouseMove = (e) => {
    if (!isDraggingDivider) return;

    const container = e.currentTarget.parentElement;
    const containerWidth = container.offsetWidth;
    const newLeftWidth = (e.clientX / containerWidth) * 100;

    // Constrain between 30% and 80%
    const constrainedWidth = Math.max(30, Math.min(80, newLeftWidth));
    setLeftWidth(constrainedWidth);
  };

  const handleDividerMouseUp = () => {
    setIsDraggingDivider(false);
  };

  // Add global mouse event listeners for divider dragging
  React.useEffect(() => {
    if (isDraggingDivider) {
      const handleMouseMove = (e) => {
        const container = document.querySelector('[data-resizable-container]');
        if (container) {
          const containerWidth = container.offsetWidth;
          const newLeftWidth = (e.clientX / containerWidth) * 100;
          const constrainedWidth = Math.max(30, Math.min(80, newLeftWidth));
          setLeftWidth(constrainedWidth);
        }
      };

      const handleMouseUp = () => {
        setIsDraggingDivider(false);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDraggingDivider]);

  // --- HELPER FUNCTIONS ---
  const trimUrl = (url, maxLength = 30) => {
    try {
      const { hostname, pathname } = new URL(url);
      const formattedUrl = `${hostname}${pathname}`;
      return formattedUrl.length > maxLength
        ? `${formattedUrl.slice(0, maxLength)}...`
        : formattedUrl;
    } catch {
      return url.length > maxLength ? `${url.slice(0, maxLength)}...` : url;
    }
  };

  const getBadgeClass = (type) => {
    switch (type) {
      case "outcome":
        return "bg-primary";
      case "hallucination":
        return "bg-danger";
      case "doubtfulness":
        return "bg-warning";
      case "ignore":
        return "bg-secondary";
      default:
        return "bg-secondary";
    }
  };

  const handleSavePointsAndFeedback = async () => {
    if (!selectedNode || !sessionId) return;

    try {
      const journeysRef = collection(db, "sessions", sessionId, "userJourneys");
      const journeysSnapshot = await getDocs(journeysRef);
      let targetJourneyDoc = null;
      let targetImageDoc = null;

      // Find the correct journey and image
      for (const journeyDoc of journeysSnapshot.docs) {
        const imagesRef = collection(journeyDoc.ref, "images");
        const imagesSnapshot = await getDocs(imagesRef);

        const imageDoc = imagesSnapshot.docs.find(doc =>
          doc.data().imageUrl === selectedNode.imageUrl
        );

        if (imageDoc) {
          targetJourneyDoc = journeyDoc;
          targetImageDoc = imageDoc;
          break;
        }
      }

      if (!targetJourneyDoc || !targetImageDoc) {
        throw new Error("Image not found in any journey");
      }

      // Convert points array to response format
      const response = points.map(point => {
        // Sanitize each point to ensure no undefined values
        const sanitizedPoint = {
          type: point.type || "other", // Default type if undefined
          text: point.text || "", // Empty string if text is undefined
          kind: point.type || "other" // For compatibility
        };

        // Only add dark_pattern if it exists and is not undefined
        if (point.dark_pattern !== undefined) {
          sanitizedPoint.dark_pattern = point.dark_pattern;
        }

        // Only add laws if they exist and are not undefined
        if (Array.isArray(point.laws)) {
          sanitizedPoint.laws = point.laws;
        }

        // Only add coordinates if they exist
        if (point.coordinates !== undefined) {
          sanitizedPoint.coordinates = point.coordinates;
        }

        // Add priority to the sanitized point
        sanitizedPoint.priority = point.priority || 1;

        return sanitizedPoint;
      });

      // console.log("Saving response data:", JSON.stringify(response));

      // Ensure feedback text is not undefined
      const sanitizedFeedback = feedbackText || "";

      const imageRef = doc(targetJourneyDoc.ref, "images", targetImageDoc.id);
      await updateDoc(imageRef, {
        response: response,
        text_feedback: sanitizedFeedback
      });

      setSelectedNode(prev => ({
        ...prev,
        response: response,
        text_feedback: sanitizedFeedback
      }));

      alert("Points and Feedback saved successfully!");
    } catch (error) {
      console.error("Error saving points and feedback:", error);
      alert(`Failed to save points and feedback: ${error.message}`);
    }
  };

  const handleRegenerateBasedOnFeedback = async () => {
    if (!selectedNode || !feedbackText) {
      alert("Please provide feedback text before regenerating");
      return;
    }

    try {
      // console.log("Starting regeneration with feedback:", feedbackText);

      // Find the correct journey and image first to get the image ID
      const journeysRef = collection(db, "sessions", sessionId, "userJourneys");
      const journeysSnapshot = await getDocs(journeysRef);

      let targetJourneyDoc = null;
      let targetImageDoc = null;

      // Search for the image in all journeys
      for (const journeyDoc of journeysSnapshot.docs) {
        const imagesRef = collection(journeyDoc.ref, "images");
        const imagesSnapshot = await getDocs(imagesRef);

        const imageDoc = imagesSnapshot.docs.find(doc =>
          doc.data().imageUrl === selectedNode.imageUrl
        );

        if (imageDoc) {
          targetJourneyDoc = journeyDoc;
          targetImageDoc = imageDoc;
          break;
        }
      }

      if (!targetImageDoc) {
        throw new Error("Image not found in database");
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/regerate_based_on_feedback`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            feedback: feedbackText,
            outcome: points.filter(p => p.type === "outcome").map(p => p.text),
            imageUrl: selectedNode.imageUrl,
            image_id: targetImageDoc.id
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      // console.log("API Response:", data);

      if (data.status !== "success" || !Array.isArray(data.response)) {
        throw new Error("Invalid response format from server");
      }

      // Prepare the update data from the API response
      const updateData = {
        response: data.response,
        text_feedback: feedbackText
      };

      // console.log("Updating Firebase with:", updateData);

      // Update Firebase
      const imageRef = doc(targetJourneyDoc.ref, "images", targetImageDoc.id);
      await updateDoc(imageRef, updateData);

      // Fetch the updated document to ensure we have the latest data
      const updatedDoc = await getDoc(imageRef);
      const updatedData = updatedDoc.data();

      // console.log("Updated Firebase document:", updatedData);

      // Create new points array from the updated data
      const newPoints = (updatedData.response || []).map(point => ({
        text: point.text,
        type: point.type,
        laws: point.laws || [],
        priority: point.priority || 1, // Add priority with default value 1
        selected: false
      }));

      // Update local state with the fresh data
      setPoints(newPoints);

      // Update selected node with the fresh data
      setSelectedNode(prev => ({
        ...prev,
        response: updatedData.response || [],
        text_feedback: feedbackText
      }));

      // Refresh the entire session data
      const sessionDocRef = doc(db, "sessions", sessionId);
      const sessionSnapshot = await getDoc(sessionDocRef);

      if (sessionSnapshot.exists()) {
        const freshSessionData = sessionSnapshot.data();
        setSession(freshSessionData);
        // console.log("Session refreshed with new data:", freshSessionData);
      }

      alert("Points regenerated and feedback saved successfully!");
    } catch (error) {
      console.error("Error in regeneration process:", error);
      alert(`Failed to regenerate points: ${error.message}`);
    }
  };

  const extractDomain = (url) => {
    try {
      if (!url || typeof url !== "string" || !/^https?:\/\//i.test(url)) {
        return url;
      }
      const { hostname } = new URL(url);
      return hostname.replace("www.", "");
    } catch (error) {
      console.error("Error processing URL:", url, error.message);
      return url;
    }
  };

  // Helper function to generate unique IDs for nodes
  let nodeIdCounter = 0;
  const getNodeId = () => `node-${nodeIdCounter++}`;

  // New horizontal journey + vertical images flow renderer (matching Figma design)
  const renderVerticalTreeFlow = (sessionData) => {
    // Reset counter for consistent IDs
    nodeIdCounter = 0;

    let journeys = Array.isArray(sessionData.userJourneys) ? sessionData.userJourneys : [];

    // Sort journeys by createdAt timestamp (earliest first)
    journeys = journeys.sort((a, b) => {
      const timeA = a.createdAt ? new Date(a.createdAt.seconds * 1000 + (a.createdAt.nanoseconds || 0) / 1000000) : new Date(0);
      const timeB = b.createdAt ? new Date(b.createdAt.seconds * 1000 + (b.createdAt.nanoseconds || 0) / 1000000) : new Date(0);
      return timeA - timeB;
    });

    // Sort images within each journey by createdAt timestamp (earliest first)
    journeys = journeys.map(journey => ({
      ...journey,
      images: journey.images ? journey.images.sort((a, b) => {
        const timeA = a.createdAt ? new Date(a.createdAt.seconds * 1000 + (a.createdAt.nanoseconds || 0) / 1000000) : new Date(0);
        const timeB = b.createdAt ? new Date(b.createdAt.seconds * 1000 + (b.createdAt.nanoseconds || 0) / 1000000) : new Date(0);
        return timeA - timeB;
      }) : []
    }));

    let domain = "Invalid Domain";
    if (sessionData?.domainName) {
      try {
        domain = extractDomain(sessionData.domainName);
      } catch (error) {
        console.error("Failed to extract domain:", sessionData.domainName, error.message);
      }
    }

    const nodes = [];
    const edges = [];

    // Calculate layout dimensions based on available space
    const minJourneySpacing = 250;
    const maxJourneySpacing = 350;
    const availableWidth = 1200; // Approximate available width

    let journeySpacing = minJourneySpacing;
    if (journeys.length > 0) {
      const totalDesiredWidth = journeys.length * maxJourneySpacing;
      if (totalDesiredWidth <= availableWidth) {
        journeySpacing = maxJourneySpacing;
      } else {
        journeySpacing = Math.max(minJourneySpacing, availableWidth / journeys.length);
      }
    }

    const totalWidth = journeys.length * journeySpacing;
    const startX = Math.max(50, (availableWidth - totalWidth) / 2); // Center the journeys with minimum margin

    // Removed category labels as requested

    // Root domain node (centered at top)
    const rootId = getNodeId();
    const domainX = startX + (totalWidth / 2) - 100; // Center better
    nodes.push({
      id: rootId,
      position: { x: domainX, y: 80 },
      data: { label: domain, nodeType: 'domain' },
      style: {
        background: '#E0E7FF',
        color: '#3730A3',
        border: '2px solid #C7D2FE',
        borderRadius: '12px',
        padding: '12px 20px',
        fontSize: '16px',
        fontWeight: 'bold',
        minWidth: '200px',
        textAlign: 'center'
      },
      sourcePosition: 'bottom',
      targetPosition: 'top'
    });

    // Journey nodes (horizontal layout)
    const journeyY = 250;
    const imageStartY = 400;
    const imageSpacing = 120;

    // Removed Journeys and Screens labels as requested

    journeys.forEach((journey, journeyIndex) => {
      // Journey node positioned horizontally
      const journeyId = getNodeId();
      const journeyX = startX + (journeyIndex * journeySpacing);

      nodes.push({
        id: journeyId,
        position: { x: journeyX, y: journeyY },
        data: { label: journey.journeyName, nodeType: 'journey' },
        style: {
          background: '#D1FAE5',
          color: '#065F46',
          border: '2px solid #A7F3D0',
          borderRadius: '10px',
          padding: '10px 16px',
          fontSize: '14px',
          fontWeight: '600',
          minWidth: '180px',
          textAlign: 'center'
        },
        sourcePosition: 'bottom',
        targetPosition: 'top'
      });

      // Edge from root to journey
      edges.push({
        id: `edge-${rootId}-${journeyId}`,
        source: rootId,
        target: journeyId,
        type: 'smoothstep',
        animated: true,
        style: { strokeWidth: 2, stroke: '#6B7280' }
      });

      // Image nodes for this journey (vertical layout below each journey)
      if (journey.images && journey.images.length > 0) {
        let previousImageId = null;

        journey.images.forEach((image, imageIndex) => {
          const imageId = getNodeId();
          const imageX = journeyX; // Same X as journey
          const imageY = imageStartY + (imageIndex * imageSpacing);

          nodes.push({
            id: imageId,
            position: { x: imageX, y: imageY },
            data: {
              label: image.pageName || image.comprehensive_result?.categories?.webpage_category || `Image ${imageIndex + 1}`,
              nodeType: 'image',
              details: {
                id: image.id,
                url: image.url || image.Url || "Unknown URL",
                type: image.type,
                note: image.note,
                imageUrl: image.imageUrl || image.image_url,
                outcome: image.outcome || null,
                feedback: image.feedback || "",
                recommendation: image.recommendation || null,
                pageName: image.pageName || image.comprehensive_result?.categories?.webpage_category || "Unknown Page",
                journeyName: journey.journeyName,
                bounding_box_url: image.bounding_box_url || null,
                agents_response: image.agents_response || null,
                comprehensive_result: image.comprehensive_result || null,
              }
            },
            style: {
              background: '#FEE2E2',
              color: '#991B1B',
              border: '2px solid #FECACA',
              borderRadius: '8px',
              padding: '8px 14px',
              fontSize: '12px',
              fontWeight: '500',
              minWidth: '160px',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                background: '#FECACA',
                transform: 'scale(1.02)'
              }
            },
            sourcePosition: 'bottom',
            targetPosition: 'top'
          });

          // Edge connections
          if (imageIndex === 0) {
            // Edge from journey to first image
            edges.push({
              id: `edge-${journeyId}-${imageId}`,
              source: journeyId,
              target: imageId,
              type: 'smoothstep',
              animated: true,
              style: { strokeWidth: 2, stroke: '#6B7280' }
            });
          } else if (previousImageId) {
            // Edge from previous image to current image
            edges.push({
              id: `edge-${previousImageId}-${imageId}`,
              source: previousImageId,
              target: imageId,
              type: 'smoothstep',
              animated: true,
              style: { strokeWidth: 2, stroke: '#6B7280' }
            });
          }

          previousImageId = imageId;
        });
      }
    });

    return { nodes, edges };
  };

  const renderReactFlowData = (sessionData) => {
    // Ensure we always work with an array for userJourneys.
    let journeys = Array.isArray(sessionData.userJourneys) ? sessionData.userJourneys : [];

    // Sort journeys by createdAt timestamp (earliest first)
    journeys = journeys.sort((a, b) => {
      const timeA = a.createdAt ? new Date(a.createdAt.seconds * 1000 + (a.createdAt.nanoseconds || 0) / 1000000) : new Date(0);
      const timeB = b.createdAt ? new Date(b.createdAt.seconds * 1000 + (b.createdAt.nanoseconds || 0) / 1000000) : new Date(0);
      return timeA - timeB;
    });

    // Sort images within each journey by createdAt timestamp (earliest first)
    journeys = journeys.map(journey => ({
      ...journey,
      images: journey.images ? journey.images.sort((a, b) => {
        const timeA = a.createdAt ? new Date(a.createdAt.seconds * 1000 + (a.createdAt.nanoseconds || 0) / 1000000) : new Date(0);
        const timeB = b.createdAt ? new Date(b.createdAt.seconds * 1000 + (b.createdAt.nanoseconds || 0) / 1000000) : new Date(0);
        return timeA - timeB;
      }) : []
    }));

    let domain = "Invalid Domain";
    if (sessionData?.domainName) {
      try {
        domain = extractDomain(sessionData.domainName);
      } catch (error) {
        console.error("Failed to extract domain:", sessionData.domainName, error.message);
      }
    }
    const nodeHeight = 150;
    const verticalSpacing = 10;
    const journeySpacing = 50;
    let yOffset = 0;

    const nodes = [
      {
        id: "horizontal-1",
        sourcePosition: "right",
        type: "input",
        data: { label: domain },
        position: { x: 0, y: 80 },
      },
      ...journeys.flatMap((journey, jIndex) => {
        const journeyNodes = [];
        journeyNodes.push({
          id: `horizontal-${jIndex + 2}`,
          sourcePosition: "right",
          targetPosition: "left",
          data: { label: journey.journeyName },
          position: { x: 250, y: yOffset },
        });

        journey.images.forEach((image, iIndex) => {
          journeyNodes.push({
            id: `horizontal-${journeys.length + jIndex * 10 + iIndex + 2}`,
            sourcePosition: "right",
            targetPosition: "left",
            data: {
              label: image.pageName || image.comprehensive_result?.categories?.webpage_category || "Unknown Page",
              details: {
                id: image.id,
                url: image.url || image.Url || "Unknown URL",
                type: image.type,
                note: image.note,
                imageUrl: image.imageUrl || image.image_url,
                outcome: image.outcome || null,
                feedback: image.feedback || "",
                recommendation: image.recommendation || null,
                pageName: image.pageName || image.comprehensive_result?.categories?.webpage_category || "Unknown Page",
                journeyName: journey.journeyName,
                bounding_box_url: image.bounding_box_url || null,
                agents_response: image.agents_response || null,
                comprehensive_result: image.comprehensive_result || null,
              },
            },
            position: {
              x: 500,
              y: yOffset + (iIndex + 1) * (nodeHeight + verticalSpacing),
            },
          });
        });

        yOffset +=
          (journey.images.length + 1) * (nodeHeight + verticalSpacing) + journeySpacing;
        return journeyNodes;
      }),
    ];

    const edges = [
      ...journeys.map((_, index) => ({
        id: `horizontal-e1-${index + 2}`,
        source: "horizontal-1",
        target: `horizontal-${index + 2}`,
        type: "smoothstep",
        animated: true,
      })),
      ...journeys.flatMap((journey, jIndex) =>
        journey.images.map((_, iIndex) => ({
          id: `horizontal-e${jIndex + 2}-${journeys.length + jIndex * 10 + iIndex + 2
            }`,
          source: `horizontal-${jIndex + 2}`,
          target: `horizontal-${journeys.length + jIndex * 10 + iIndex + 2
            }`,
          type: "smoothstep",
          animated: true,
        }))
      ),
    ];

    return { nodes, edges };
  };

  const handleNodeClick = async (event, node) => {
    // Handle vertical tree flow nodes
    if (node.data.nodeType === 'image' && node.data.details) {
      // Switch to detailed review tab
      setActiveTab('detailed-review');
      setViewMode('split');

      // Find the index of this image in allImages array
      const imageIndex = allImages.findIndex(img =>
        img.id === node.data.details.id ||
        img.imageUrl === node.data.details?.imageUrl ||
        img.imageUrl === node.data.details?.image_url
      );

      if (imageIndex !== -1) {
        setCurrentImageIndex(imageIndex);
      }

      await handleNodeSelection(node.data.details);
      return;
    }

    // Handle original horizontal flow nodes
    if (
      node.id.startsWith("horizontal-") &&
      node.id !== "horizontal-1" &&
      node.data.details
    ) {
      try {
        // Find the index of this image in allImages array
        const imageIndex = allImages.findIndex(img =>
          img.id === node.data.details.id ||
          img.imageUrl === node.data.details?.imageUrl ||
          img.imageUrl === node.data.details?.image_url
        );

        if (imageIndex !== -1) {
          setCurrentImageIndex(imageIndex);
        }

        // Switch to split view mode
        setViewMode('split');
        setActiveTab('detailed-review');

        // Reuse handleNodeSelection logic
        await handleNodeSelection(node.data.details);

      } catch (error) {
        console.error("Error handling node click:", error);
      }
    } else {
      setSelectedNode(null);
      setFeedbackText("");
      setPoints([]);
    }
  };

  // --- FIRESTORE UPDATE: Save Session Changes ---
  const handleSaveChanges = async () => {
    const sessionDocRef = doc(db, "sessions", sessionId);
    try {
      // Update the entire session document with the local state
      await updateDoc(sessionDocRef, { ...session });
      alert("Changes saved successfully!");
    } catch (error) {
      console.error("Error saving changes:", error);
      alert("Failed to save changes.");
    }
  };

  // (The functions below remain largely unchanged.
  // They update the local session state, which you can then persist with handleSaveChanges.)

  const handleProcessImage = async () => {
    if (!selectedNode || !sessionId) return;
    setIsProcessingImage(true);

    try {
      // Get metadata from API
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/get_image_metadata`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            image_url: selectedNode.imageUrl,
            url: selectedNode.url,
            type: selectedNode.type,
            note: selectedNode.note,
          }),
        }
      );
      const metadata = await response.json();

      // Find the journey and image documents
      const journeysRef = collection(db, "sessions", sessionId, "userJourneys");
      const journeysSnapshot = await getDocs(journeysRef);
      let targetJourneyDoc = null;
      let targetImageDoc = null;

      for (const journeyDoc of journeysSnapshot.docs) {
        const imagesRef = collection(journeyDoc.ref, "images");
        const imagesSnapshot = await getDocs(imagesRef);

        const imageDoc = imagesSnapshot.docs.find(doc =>
          doc.data().imageUrl === selectedNode.imageUrl
        );

        if (imageDoc) {
          targetJourneyDoc = journeyDoc;
          targetImageDoc = imageDoc;
          break;
        }
      }

      if (!targetJourneyDoc || !targetImageDoc) {
        throw new Error("Image not found in any journey");
      }

      // Convert API response to use 'kind' instead of 'type'
      const formattedResponse = (metadata.response || []).map(item => ({
        kind: item.type || item.kind,
        text: item.text
      }));

      // Update the image document with the response array
      const imageRef = doc(targetJourneyDoc.ref, "images", targetImageDoc.id);
      await updateDoc(imageRef, {
        response: formattedResponse
      });

      // Fetch the updated image data
      const updatedImageDoc = await getDoc(imageRef);
      const updatedImageData = updatedImageDoc.data();

      // Update selected node state
      setSelectedNode(prev => ({
        ...prev,
        ...updatedImageData
      }));

      // Update points display from response array
      const newPoints = formattedResponse.map(point => ({
        text: point.text,
        type: point.kind, // Convert 'kind' to 'type' for UI consistency
        laws: [],
        priority: 1, // Set default priority to 1 for new points
        selected: false
      }));
      setPoints(newPoints);

    } catch (error) {
      console.error("Error processing image:", error);
    } finally {
      setIsProcessingImage(false);
    }
  };

  const handleGetRecommendation = async () => {
    if (!selectedNode.outcome) return;
    setIsRecommendationLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/generate_recommendation`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ outcome: selectedNode.outcome }),
        }
      );
      const { recommendation } = await response.json();
      setSession((prev) => {
        const updatedSession = { ...prev };
        (updatedSession.userJourneys || []).forEach((journey) => {
          journey.images.forEach((image) => {
            if (image.imageUrl === selectedNode.imageUrl) {
              image.recommendation = recommendation;
            }
          });
        });
        return updatedSession;
      });
      setSelectedNode((prev) => ({
        ...prev,
        recommendation,
      }));
    } catch (error) {
      console.error("Error fetching recommendation:", error);
    } finally {
      setIsRecommendationLoading(false);
    }
  };

  const saveFeedback = (type) => {
    if (!selectedNode) return;
    setSession((prev) => {
      const updatedSession = { ...prev };
      (updatedSession.userJourneys || []).forEach((journey) => {
        journey.images.forEach((image) => {
          if (image.imageUrl === selectedNode.imageUrl) {
            image[`${type}_feedback`] = feedback[type];
            if (feedback[type] === "no") {
              const additionalDetail =
                type === "hallucination" ? image.hallucination : image.doubtful;
              if (additionalDetail) {
                image.outcome = image.outcome
                  ? `${image.outcome}\n\n${type.toUpperCase()} DETAILS:\n${additionalDetail}`
                  : `${type.toUpperCase()} DETAILS:\n${additionalDetail}`;
              }
            }
          }
        });
      });
      return updatedSession;
    });

    setSelectedNode((prev) => {
      if (!prev) return null;
      const additionalDetail =
        type === "hallucination" ? prev.hallucination : prev.doubtful;
      let updatedOutcome = prev.outcome || "";
      if (feedback[type] === "no" && additionalDetail) {
        updatedOutcome = updatedOutcome
          ? `${updatedOutcome}\n\n${type.toUpperCase()} DETAILS:\n${additionalDetail}`
          : `${type.toUpperCase()} DETAILS:\n${additionalDetail}`;
      }
      return {
        ...prev,
        [`${type}_feedback`]: feedback[type],
        outcome: updatedOutcome,
      };
    });
    alert(`${type} feedback saved successfully, and outcome updated!`);
  };

  const handleUpdateFeedback = (e) => {
    const updatedFeedback = e.target.value;
    setSelectedNode((prev) => ({
      ...prev,
      feedback: updatedFeedback,
    }));
    setSession((prev) => {
      const updatedSession = { ...prev };
      (updatedSession.userJourneys || []).forEach((journey) => {
        journey.images.forEach((image) => {
          if (image.imageUrl === selectedNode.imageUrl) {
            image.feedback = updatedFeedback;
          }
        });
      });
      return updatedSession;
    });
  };

  const handleExportProperties = async () => {
    if (!selectedNode) return;

    const capturedAt = selectedNode.createdAt || selectedNode.imageCreatedAt;
    let formattedDate = "None";
    if (capturedAt) {
      if (capturedAt.seconds) formattedDate = new Date(capturedAt.seconds * 1000).toLocaleString();
      else if (capturedAt instanceof Date) formattedDate = capturedAt.toLocaleString();
      else if (typeof capturedAt === 'string') formattedDate = new Date(capturedAt).toLocaleString();
      else if (capturedAt.toDate) formattedDate = capturedAt.toDate().toLocaleString();
    }

    const imageHash = selectedNode.image_hash || (selectedNode.imageUrl ? "H-" + Math.abs(selectedNode.imageUrl.split('').reduce((a, b) => { a = ((a << 5) - a) + b.charCodeAt(0); return a | 0 }, 0)).toString(16) : "None");
    const journeyName = selectedNode.journeyName || 'None';
    const currentJourney = session?.userJourneys?.find(j => j.name === selectedNode.journeyName);
    const createdBy = currentJourney?.createdBy || session?.createdBy || 'None';
    const pageName = selectedNode.comprehensive_result?.categories?.webpage_category || selectedNode.pageName || 'None';
    const websiteCategory = selectedNode.comprehensive_result?.categories?.website_category || selectedNode.website_category || 'None';
    const imageUrl = selectedNode.imageUrl || selectedNode.image_url || '';

    // Calculate the filtered visible navigation to send to the backend
    let visibleNavigation = [];
    if (selectedJourneyNavigation && selectedJourneyNavigation.length > 0) {
      let captureIndex = selectedJourneyNavigation.findIndex(nav =>
        (nav.url && (selectedNode.url === nav.url || selectedNode.Url === nav.url)) ||
        (nav.to && (selectedNode.url === nav.to || selectedNode.Url === nav.to)) ||
        (nav.from && (selectedNode.url === nav.from || selectedNode.Url === nav.from))
      );
      if (captureIndex === -1) captureIndex = Math.min(2, selectedJourneyNavigation.length - 1);

      const startIndex = Math.max(0, captureIndex - 2);
      const endIndex = Math.min(selectedJourneyNavigation.length, captureIndex + 3);
      visibleNavigation = selectedJourneyNavigation.slice(startIndex, endIndex);
    }

    const exportPayload = {
      title: "Dark pattern analysis",
      journeyName,
      createdBy,
      url: selectedNode.url || selectedNode.Url || 'None',
      pageName,
      websiteCategory,
      capturedAt: formattedDate,
      imageHash,
      encryptionType: "Google-managed (AES-256 encryption)",
      imageUrl,
      visibleNavigation
    };

    try {
      setIsExportPDFLoading(true);
      toast.info("Generating PDF, please wait...");

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/image-property-export`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(exportPayload)
      });

      if (!response.ok) {
        throw new Error(`Failed to generate PDF: ${response.statusText}`);
      }

      // Handle the blob response from the server to initiate file download
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', `Dark_Pattern_Analysis_${selectedNode.id || 'export'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      toast.success("PDF exported successfully!");

    } catch (error) {
      console.error("Error exporting PDF:", error);
      toast.error("An error occurred while exporting the PDF.");
    } finally {
      setIsExportPDFLoading(false);
    }
  };

  // Function to handle note text changes and update Firebase
  const handleNoteChange = async (e) => {
    const updatedNote = e.target.value;

    // Update local state first
    setSelectedNode((prev) => ({
      ...prev,
      note: updatedNote,
    }));

    // Update session state
    setSession((prev) => {
      const updatedSession = { ...prev };
      (updatedSession.userJourneys || []).forEach((journey) => {
        journey.images.forEach((image) => {
          if (image.imageUrl === selectedNode.imageUrl) {
            image.note = updatedNote;
          }
        });
      });
      return updatedSession;
    });

    try {
      // Find the journey and image document to update in Firebase
      const journeysRef = collection(db, "sessions", sessionId, "userJourneys");
      const journeysSnapshot = await getDocs(journeysRef);

      let targetJourneyDoc = null;
      let targetImageDoc = null;

      for (const journeyDoc of journeysSnapshot.docs) {
        const imagesRef = collection(journeyDoc.ref, "images");
        const imagesSnapshot = await getDocs(imagesRef);

        const imageDoc = imagesSnapshot.docs.find(doc =>
          doc.data().imageUrl === selectedNode.imageUrl
        );

        if (imageDoc) {
          targetJourneyDoc = journeyDoc;
          targetImageDoc = imageDoc;
          break;
        }
      }

      if (targetJourneyDoc && targetImageDoc) {
        const imageRef = doc(targetJourneyDoc.ref, "images", targetImageDoc.id);
        await updateDoc(imageRef, {
          note: updatedNote
        });
        // console.log("Note updated successfully in Firebase");
      } else {
        // console.warn("Could not find image document to update note");
      }
    } catch (error) {
      // console.error("Error updating note in Firebase:", error);
    }
  };


  // --- Combining Flow Data ---
  const flowData = React.useMemo(() => {
    if (session && session.userJourneys && Array.isArray(session.userJourneys) && session.userJourneys.length > 0) {
      return renderVerticalTreeFlow(session);
    }
    return { nodes: [], edges: [] };
  }, [session]);
  const combinedNodes = [
    ...flowData.nodes,
    ...(journeyFlowData
      ? journeyFlowData.nodes.map((node) => ({
        ...node,
        position: { x: node.position.x + 600, y: node.position.y },
        id: `journey-${node.id}`,
      }))
      : []),
  ];
  const combinedEdges = [
    ...flowData.edges,
    ...(journeyFlowData
      ? journeyFlowData.edges.map((edge) => ({
        ...edge,
        id: `journey-${edge.id}`,
      }))
      : []),
  ];

  const handleBoundingBox = async () => {
    if (!selectedNode.outcome) return;
    setIsBoundingBoxLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/generate_bounding_box`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            imageUrl: selectedNode.imageUrl,
            outcome: selectedNode.outcome,
          }),
        }
      );
      const { bounding_box_url } = await response.json();
      setSession((prev) => {
        const updatedSession = { ...prev };
        (updatedSession.userJourneys || []).forEach((journey) => {
          journey.images.forEach((image) => {
            if (image.imageUrl === selectedNode.imageUrl) {
              image.bounding_box_url = bounding_box_url;
            }
          });
        });
        return updatedSession;
      });
      setSelectedNode((prev) => ({
        ...prev,
        bounding_box_url,
      }));
    } catch (error) {
      console.error("Error generating bounding box:", error);
    } finally {
      setIsBoundingBoxLoading(false);
    }
  };

  const handleAddToReasoningDB = async () => {
    if (!selectedNode || !sessionId) return;
    setIsAddingToDBLoading(true);
    setAddToDBStatus(null);

    try {
      // Find the journey and image documents in Firestore
      const journeysRef = collection(db, "sessions", sessionId, "userJourneys");
      const journeysSnapshot = await getDocs(journeysRef);

      let journeyId = null;
      let imageId = null;

      for (const journeyDoc of journeysSnapshot.docs) {
        const imagesRef = collection(journeyDoc.ref, "images");
        const imagesSnapshot = await getDocs(imagesRef);

        const imageDoc = imagesSnapshot.docs.find(doc =>
          doc.data().imageUrl === selectedNode.imageUrl
        );

        if (imageDoc) {
          journeyId = journeyDoc.id;
          imageId = imageDoc.id;
          break;
        }
      }

      if (!journeyId || !imageId) {
        throw new Error("Could not find journey ID or image ID for the selected node");
      }

      // console.log("Adding to DB with:", { sessionId, journeyId, imageId });

      // Call the record_reasoning_data endpoint
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/record_reasoning_data`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            session_id: sessionId,
            journey_id: journeyId,
            image_id: imageId
          }),
        }
      );

      const result = await response.json();

      if (response.ok) {
        setAddToDBStatus({
          success: true,
          message: result.message || "Successfully added to Reasoning Database"
        });
      } else {
        throw new Error(result.message || "Failed to add to Reasoning Database");
      }
    } catch (error) {
      console.error("Error adding to Reasoning Database:", error);
      setAddToDBStatus({
        success: false,
        message: error.message || "Failed to add to Reasoning Database"
      });
    } finally {
      setIsAddingToDBLoading(false);
    }
  };

  // Define a list of agent names
  const agentNames = [
    'Preselection agent v1.4',
    'Hidden legalese stipulations agent v0.2',
    'Potential false social proof agent v1.0',
    'Potential false urgency agent v1.0',
    'Potential false scarcity agent v1.0',
    'Potentially false endorsement claims agent v1.0',
    'Potentially false trust marks agent v1.0',
    'Potentially false closure claim agent v1.0',
    'Potentially false free offer agent v1.0',
    'Confirm shaming agent v0.3',
    'Hidden costs agent v0.4',
    'Obstruction agent v0.5',
    'Generalised Obstruction agent v0.62',
    'Visual interference v0.4',
    'Potential disguised ads v0.31',
    'Hidden Subscription v0.1',
    'Trick Wording v0.1',
    'Misleading Pricing Information v0.1',
    'Hard to Cancel v0.1',
    'Potential false code of conduct signatory v0.1',
    'Bait and switch v0.1',
    'Potential false competition claim v0.1',
    'Ranking disclosure v0.1',
    'Unclear terms and policies v0.1',
    'Delayed order acknowledgement v0.1',
    'Trader Identity Detector v0.1',
    'Comparison prevention v0.1',
    'Nagging v0.1',
    'Forced action v0.1',
    'Coercion -commercial V0.1',
    'Privacy coercion',
    'Platform or service coercion',
    'Generalized falsification',
    'Bait advertising',
    'Generalized misdirection',
    'Misleading omissions',
    'Generalized sneaking',
    'Limiting consent',
  ];

  // Function to fetch XAI details
  const fetchXAIDetails = async (point) => {
    if (!selectedNode || !point) return;

    setIsXAILoading(true);
    try {
      // Find the image document ID and journey ID from Firebase
      const journeysRef = collection(db, "sessions", sessionId, "userJourneys");
      const journeysSnapshot = await getDocs(journeysRef);
      let imageId = null;
      let journeyId = null;
      let imageDoc = null;
      let journeyDoc = null;

      // Find the correct journey and image
      for (const jDoc of journeysSnapshot.docs) {
        const imagesRef = collection(jDoc.ref, "images");
        const imagesSnapshot = await getDocs(imagesRef);

        const iDoc = imagesSnapshot.docs.find(doc =>
          doc.data().imageUrl === selectedNode.imageUrl
        );

        if (iDoc) {
          imageId = iDoc.id; // This is the Firebase document ID we need
          journeyId = jDoc.id; // Get the journey document ID
          imageDoc = iDoc;
          journeyDoc = jDoc;
          break;
        }
      }

      if (!imageId || !journeyId) {
        throw new Error("Image or journey not found in Firebase");
      }

      // console.log("Using Firebase IDs:", {
      //   imageId,
      //   journeyId,
      //   pointText: point.text // Log the point text being sent
      // });

      // Get current image data and check if XAI results are cached
      const imageData = imageDoc.data();
      const response = imageData.response || [];

      // Find the matching point in the response array
      const pointIndex = response.findIndex(p => p.text === point.text);

      if (pointIndex !== -1 && response[pointIndex].xai_validation && response[pointIndex].xai_path_b) {
        // Use cached results if available
        // console.log("Using cached XAI results from Firebase");
        setXAIDetails(response[pointIndex].xai_validation);
        setPathBAgents(response[pointIndex].xai_path_b);
        setIsXAILoading(false);
        return;
      }

      // If not cached, fetch from API
      // console.log("No cached XAI results found, fetching from API");

      // First endpoint - get_xai_details
      const response1 = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/get_xai_details`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            sessionId: sessionId,
            userJourneyId: journeyId,
            imageId: imageId,
            point: point.text // Only send the point text
          })
        }
      );

      if (!response1.ok) {
        const errorData = await response1.json();
        throw new Error(`XAI Details API Error: ${errorData.message || response1.statusText}`);
      }

      const data1 = await response1.json();
      // console.log("XAI Details API Response:", data1);
      setXAIDetails(data1);

      // Second endpoint - get_xai_details_for_path_b
      const response2 = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/get_xai_details_for_path_b`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            point: point.text // Only send the point text
          })
        }
      );

      if (!response2.ok) {
        const errorData = await response2.json();
        throw new Error(`Path B API Error: ${errorData.message || response2.statusText}`);
      }

      const data2 = await response2.json();
      if (data2.status === 'success') {
        setPathBAgents(data2.data);
      } else {
        // console.warn("Path B API returned non-success status:", data2);
      }

      // console.log("XAI Details received:", data1);
      // console.log("Path B agents received:", data2.data);

      // Save results to Firebase for future use
      const updatedResponse = [...response];
      if (pointIndex !== -1) {
        // Update existing point
        updatedResponse[pointIndex] = {
          ...updatedResponse[pointIndex],
          xai_validation: data1,
          xai_path_b: data2.data
        };
      } else {
        // This shouldn't typically happen, but handle just in case
        updatedResponse.push({
          text: point.text,
          type: point.type || 'outcome',
          xai_validation: data1,
          xai_path_b: data2.data
        });
      }

      // Update Firebase with cached results
      const imageRef = doc(journeyDoc.ref, "images", imageId);
      await updateDoc(imageRef, {
        response: updatedResponse
      });

      // console.log("Saved XAI results to Firebase for future use");

    } catch (error) {
      console.error("Error fetching XAI details:", error);
      alert(`Failed to fetch XAI details: ${error.message}`);
    } finally {
      setIsXAILoading(false);
    }
  };

  // Modify the isAgentActive function to handle both paths
  const getAgentStatus = (displayName) => {
    // First check if agent is active in Path A (from agent_responses)
    const isPathA = activeAgents.some(activeAgent => {
      const cleanDisplay = displayName.toLowerCase()
        .replace(/agent|v[0-9.]+|\s+|potential(ly)?|false/g, '')
        .trim();

      const cleanActive = activeAgent.toLowerCase()
        .replace(/agent|v[0-9.]+|\s+|potential(ly)?|false/g, '')
        .trim();

      return cleanDisplay.includes(cleanActive) || cleanActive.includes(cleanDisplay);
    });

    // Then check if agent is in Path B
    const isPathB = pathBAgents.some(pathBAgent => {
      const cleanDisplay = displayName.toLowerCase()
        .replace(/agent|v[0-9.]+|\s+|potential(ly)?|false/g, '')
        .trim();

      const cleanPathB = pathBAgent.toLowerCase()
        .replace(/agent|v[0-9.]+|\s+|potential(ly)?|false/g, '')
        .trim();

      return cleanDisplay.includes(cleanPathB) || cleanPathB.includes(cleanDisplay);
    });

    return {
      isPathA,
      isPathB
    };
  };

  // Modify renderXAIFlowData to use XAI details
  const renderXAIFlowData = () => {
    // Define constants for layout
    const xSpacing = 250; // Horizontal spacing between groups
    const nodeHeight = 40;
    const verticalSpacing = 60;

    // Define group positions
    const groupX = {
      input: 0,
      step1: xSpacing,
      step2: xSpacing * 2,
      step3: xSpacing * 3,
      step4: xSpacing * 4
    };

    // Parent category to agents mapping
    const parent_category_agents = {
      'Coercing': ['Privacy coercion', 'Platform or service coercion', 'Coercion -commercial V0.1', 'Nagging v0.1', 'Forced action v0.1'],
      'Misdirecting': ['Delayed order acknowledgement v0.1', 'Potential disguised ads v0.31', 'Potential disguised ads v0.28', 'Visual interference v0.4', 'Trick Wording v0.1', 'Generalized misdirection', 'Unclear terms and policies v0.1', 'Confirm shaming agent v0.3', 'Misleading Pricing Information v0.1', 'Misleading omissions'],
      'Falsifying': ['Potential false code of conduct signatory v0.1', 'Potential false social proof agent v1.0', 'Potential false closure claim agent v1.0', 'Potential false closure claim v0.1', 'Generalized falsification', 'Potential false urgency agent v0.5', 'Potential false scarcity agent v1.0', 'Potential false endorsement claims agent v1.0', 'Potential false free offer agent v1.0', 'Potential false free offer v0.1', 'Potential false competition claim v0.1', 'Potential false urgency agent v1.0', 'Bait and switch v0.1', 'Potential false trust marks agent v1.0', 'Bait advertising'],
      'Sneaking': ['Generalized sneaking', 'Hidden Subscription v0.1', 'Preselection agent v1.4', 'Hidden legalese stipulations agent v0.2', 'Hidden costs agent v0.4'],
      'Obstructing': ['Hard to cancel v0.1', 'Obstruction agent v0.5', 'Generalised Obstruction agent v0.62', 'Comparison prevention v0.1', 'Trick Wording v0.1'],
      'Omitting': ['Lack of ranking disclosure v0.1', 'Trader Identity omission v0.1']
    };

    // Define input and step1 nodes
    const inputNodes = [
      { id: 'origin-image', data: { label: 'Image', group: 'input' }, position: { x: groupX.input, y: 0 } },
      { id: 'origin-user', data: { label: 'User Note', group: 'input' }, position: { x: groupX.input, y: nodeHeight + verticalSpacing } }
    ];

    const step1Nodes = [
      {
        id: 'extraction-1',
        data: {
          label: 'Scanner',
          group: 'step1'
        },
        position: { x: groupX.step1, y: 0 },
        style: xaiDetails?.data?.scanner === false ? {
          backgroundColor: '#ffebee',
          borderColor: '#ef5350',
          color: '#c62828',
          borderWidth: 2
        } : {}
      },
      {
        id: 'extraction-2',
        data: {
          label: 'Scanner with Context',
          group: 'step1'
        },
        position: { x: groupX.step1, y: nodeHeight + verticalSpacing },
        style: xaiDetails?.data?.Scanner_with_context === false ? {
          backgroundColor: '#ffebee',
          borderColor: '#ef5350',
          color: '#c62828',
          borderWidth: 2
        } : {}
      }
    ];

    // Create step2 nodes (parent categories) and agent nodes with proper positioning
    let currentY = 0;
    const step2Nodes = [];
    const agentNodes = [];
    const edges = [];

    // Process each parent category
    Object.entries(parent_category_agents).forEach(([category, agents], categoryIndex) => {
      // Filter active agents for this category
      const activeAgents = agents.filter(agent => {
        const { isPathA, isPathB } = getAgentStatus(agent);
        return isPathA || isPathB;
      });

      // Only create parent category node if there are active agents
      if (activeAgents.length > 0) {
        const categoryHeight = activeAgents.length * (nodeHeight + verticalSpacing);
        const parentY = currentY + (categoryHeight / 2); // Center parent node vertically

        // Add parent category node
        const parentId = `category-${categoryIndex}`;
        step2Nodes.push({
          id: parentId,
          data: { label: category, group: 'step2' },
          position: { x: groupX.step2, y: parentY },
          sourcePosition: 'right',
          targetPosition: 'left',
          style: {
            width: 150, // Fixed width for parent nodes
            padding: '10px'
          }
        });

        // Add only active agent nodes for this category
        activeAgents.forEach((agent, agentIndex) => {
          const agentId = `agent-${categoryIndex}-${agentIndex}`;
          const { isPathA, isPathB } = getAgentStatus(agent);

          // Calculate agent Y position
          const agentY = currentY + (agentIndex * (nodeHeight + verticalSpacing));

          // Check if this agent has a status in agents_response_xai
          const agentXAIStatus = xaiDetails?.data?.agents_response_xai?.[agent];

          // Determine node style based on XAI response first, then path status
          let nodeStyle = {
            borderWidth: 2,
            borderStyle: 'solid',
            width: 150,
            padding: '10px'
          };

          if (agentXAIStatus === false) {
            // Only apply red style for false agents
            nodeStyle = {
              ...nodeStyle,
              backgroundColor: '#ffebee',
              borderColor: '#ef5350',
              color: '#c62828',
              fontWeight: 'bold'
            };
          } else if (isPathA && isPathB) {
            nodeStyle = {
              ...nodeStyle,
              borderColor: '#00b74a',
              borderWidth: 2,
              backgroundColor: 'rgba(0, 183, 74, 0.2)',
              fontWeight: 'bold'
            };
          } else if (isPathA) {
            nodeStyle = {
              ...nodeStyle,
              borderColor: '#0077b6',
              borderWidth: 2,
              backgroundColor: 'rgba(0, 119, 182, 0.1)',
              fontWeight: 'bold'
            };
          } else if (isPathB) {
            nodeStyle = {
              ...nodeStyle,
              borderColor: '#ffc107',
              borderWidth: 2,
              backgroundColor: 'rgba(255, 193, 7, 0.1)',
              fontWeight: 'bold'
            };
          }

          agentNodes.push({
            id: agentId,
            data: {
              label: agent,
              active: isPathA || isPathB,
              pathA: isPathA,
              pathB: isPathB,
              group: 'step3',
              xaiStatus: agentXAIStatus
            },
            position: {
              x: groupX.step3,
              y: agentY
            },
            sourcePosition: 'right',
            targetPosition: 'left',
            style: nodeStyle
          });

          // Add edge from parent category to agent with smart routing
          edges.push({
            id: `e-${parentId}-to-${agentId}`,
            source: parentId,
            target: agentId,
            type: 'smoothstep',
            animated: true,
            style: { strokeWidth: 2 },
            data: {
              curvature: 0.4,
              offsetPercent: (agentIndex - (activeAgents.length - 1) / 2) * 5
            }
          });
        });

        // Update Y position for next category with additional spacing
        currentY += categoryHeight + (nodeHeight * 2);
      }
    });

    // Set step 4 nodes to start at the same vertical level as other step groups
    const step4StartY = 0;

    // Update Step 4 nodes positioning
    const step4Nodes = [
      {
        id: 'revised-agent-responses',
        data: {
          label: 'Revised Agent Responses',
          group: 'step4'
        },
        position: { x: groupX.step4, y: step4StartY },
        style: xaiDetails?.data?.revised_agent_responses === false ? {
          backgroundColor: '#ffebee',
          borderColor: '#ef5350',
          color: '#c62828',
          borderWidth: 2,
          width: 200,
          padding: '10px'
        } : {
          width: 200,
          padding: '10px'
        }
      },
      {
        id: 'key-highlights',
        data: {
          label: 'Key Highlights',
          group: 'step4'
        },
        position: { x: groupX.step4, y: step4StartY + nodeHeight + verticalSpacing },
        style: xaiDetails?.data?.key_highlights === false ? {
          backgroundColor: '#ffebee',
          borderColor: '#ef5350',
          color: '#c62828',
          borderWidth: 2
        } : {}
      },
      {
        id: 'actual-measurable-harm',
        data: {
          label: 'Actual Measurable Harm',
          group: 'step4'
        },
        position: { x: groupX.step4, y: step4StartY + (nodeHeight + verticalSpacing) * 2 },
        style: xaiDetails?.data?.actual_measurable_harm === false ? {
          backgroundColor: '#ffebee',
          borderColor: '#ef5350',
          color: '#c62828',
          borderWidth: 2
        } : {}
      },
      {
        id: 'outcome-fact-check',
        data: {
          label: 'Outcome Fact Check Response',
          group: 'step4'
        },
        position: { x: groupX.step4, y: step4StartY + (nodeHeight + verticalSpacing) * 3 },
        style: xaiDetails?.data?.outcome_fact_check_response === false ? {
          backgroundColor: '#ffebee',
          borderColor: '#ef5350',
          color: '#c62828',
          borderWidth: 2
        } : {}
      },
      {
        id: 'outcome-validation',
        data: {
          label: 'Outcome Validation Result',
          group: 'step4'
        },
        position: { x: groupX.step4, y: step4StartY + (nodeHeight + verticalSpacing) * 4 },
        style: xaiDetails?.data?.outcome_validation_result === false ? {
          backgroundColor: '#ffebee',
          borderColor: '#ef5350',
          color: '#c62828',
          borderWidth: 2
        } : {}
      }
    ];

    // Connect active agents to revised-agent-responses with smart routing
    const activeAgents = agentNodes.filter(node => node.data.active);
    activeAgents.forEach((agent, index) => {
      edges.push({
        id: `e-${agent.id}-to-revised`,
        source: agent.id,
        target: 'revised-agent-responses',
        type: 'smoothstep',
        animated: true,
        style: { strokeWidth: 2 },
        data: {
          // Distribute curves evenly based on number of active agents
          curvature: 0.3,
          offsetPercent: (index - (activeAgents.length - 1) / 2) * 5
        }
      });
    });

    // Add step 4 sequential connections
    edges.push(
      // Connect step 4 nodes sequentially
      {
        id: 'e-revised-to-highlights',
        source: 'revised-agent-responses',
        target: 'key-highlights',
        type: 'smoothstep',
        animated: true
      },
      {
        id: 'e-highlights-to-harm',
        source: 'key-highlights',
        target: 'actual-measurable-harm',
        type: 'smoothstep',
        animated: true
      },
      {
        id: 'e-harm-to-fact-check',
        source: 'actual-measurable-harm',
        target: 'outcome-fact-check',
        type: 'smoothstep',
        animated: true
      },
      {
        id: 'e-fact-check-to-validation',
        source: 'outcome-fact-check',
        target: 'outcome-validation',
        type: 'smoothstep',
        animated: true
      }
    );

    // Add basic flow edges
    edges.push(
      // Input to Step 1
      { id: 'e-origin-image-to-extraction-1', source: 'origin-image', target: 'extraction-1', type: 'smoothstep', animated: true },
      { id: 'e-origin-user-to-extraction-1', source: 'origin-user', target: 'extraction-1', type: 'smoothstep', animated: true },
      // Step 1 to first parent category
      { id: 'e-extraction-2-to-first-category', source: 'extraction-2', target: 'category-0', type: 'smoothstep', animated: true }
    );

    // Combine all nodes
    const nodes = [
      ...inputNodes,
      ...step1Nodes,
      ...step2Nodes,
      ...agentNodes,
      ...step4Nodes
    ];

    return {
      nodes,
      edges,
      // Add default edge options
      defaultEdgeOptions: {
        type: 'smoothstep',
        animated: true,
        style: { strokeWidth: 2 }
      }
    };
  };

  // Function to toggle XAI popup
  const toggleXAIPopup = () => {
    setIsXAIPopupOpen(prev => !prev);
  };

  /**
   * Creates a new empty point in the local state.
   * Note: The point will only be saved to Firebase when the user edits and confirms changes.
   * This allows users to cancel the creation if they don't want to add the point.
   */
  const handleAddPoint = () => {
    const newPoint = {
      text: "",
      type: "outcome",
      dark_pattern: null,
      coordinates: null,
      priority: 1, // Set default priority to 1 for new points
      selected: false
    };
    setPoints([...points, newPoint]);
  };

  const handleTextChange = (index, newText) => {
    const updatedPoints = points.map((point, i) =>
      i === index ? { ...point, text: newText } : point
    );
    setPoints(updatedPoints);
  };

  const handleTypeChange = async (index, newType) => {
    const updatedPoints = points.map((point, i) =>
      i === index ? { ...point, type: newType } : point
    );
    setPoints(updatedPoints);

    // If the selected node exists, update Firebase
    if (selectedNode) {
      try {
        setLoading(true);
        // Get reference to all user journeys
        const journeysRef = collection(db, "sessions", sessionId, "userJourneys");
        const journeysSnapshot = await getDocs(journeysRef);
        let targetJourneyDoc = null;
        let targetImageDoc = null;

        // Find the journey and image containing the selected node
        for (const journeyDoc of journeysSnapshot.docs) {
          const imagesRef = collection(journeyDoc.ref, "images");
          const imagesSnapshot = await getDocs(imagesRef);

          const imageDoc = imagesSnapshot.docs.find(doc =>
            doc.data().imageUrl === selectedNode.imageUrl
          );

          if (imageDoc) {
            targetJourneyDoc = journeyDoc;
            targetImageDoc = imageDoc;
            break;
          }
        }

        if (!targetJourneyDoc || !targetImageDoc) {
          throw new Error("Image not found in any journey");
        }

        // Get current response array
        const currentData = targetImageDoc.data();
        if (!currentData) {
          throw new Error("Image document data is undefined");
        }

        const responseArray = currentData.response || [];
        if (!Array.isArray(responseArray)) {
          throw new Error(`Invalid response format: ${JSON.stringify(responseArray)}`);
        }

        // Update the type for the specific point
        const updatedResponse = responseArray.map((point, i) => {
          if (i === index) {
            return {
              ...point,
              text: point.text || "",
              type: newType,
              kind: newType // Update both type and kind for compatibility
            };
          }
          return point;
        });

        // Update Firebase
        const imageRef = doc(targetJourneyDoc.ref, "images", targetImageDoc.id);
        await updateDoc(imageRef, {
          response: updatedResponse
        });

        // Update selected node state
        setSelectedNode(prev => ({
          ...prev,
          response: updatedResponse
        }));

        // console.log(`Point type updated to ${newType} successfully`);
      } catch (error) {
        // console.error("Error updating point type:", error, "Index:", index, "NewType:", newType);
        alert(`Failed to update point type in Firebase: ${error.message}`);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleMergePoints = async () => {
    const selectedPoints = points.filter(p => p.selected);
    if (selectedPoints.length < 2) {
      alert('Please select at least 2 points to merge');
      return;
    }

    const mergedText = selectedPoints
      .map(p => p.text)
      .filter(text => text && text.trim())
      .join('\n\n');

    // Collect all laws from selected points
    const allLaws = [];
    selectedPoints.forEach(point => {
      if (Array.isArray(point.laws)) {
        point.laws.forEach(law => {
          if (!allLaws.some(l => l.id === law.id)) {
            allLaws.push(law);
          }
        });
      }
    });

    const newPoints = points.filter(p => !p.selected).concat({
      text: mergedText,
      type: selectedPoints[0].type || "other",
      kind: selectedPoints[0].type || "other",
      laws: allLaws,
      selected: false
    });

    setPoints(newPoints);

    // If the selected node exists, update Firebase
    if (selectedNode) {
      try {
        setLoading(true);
        // Get reference to all user journeys
        const journeysRef = collection(db, "sessions", sessionId, "userJourneys");
        const journeysSnapshot = await getDocs(journeysRef);
        let targetJourneyDoc = null;
        let targetImageDoc = null;

        // Find the journey and image containing the selected node
        for (const journeyDoc of journeysSnapshot.docs) {
          const imagesRef = collection(journeyDoc.ref, "images");
          const imagesSnapshot = await getDocs(imagesRef);

          const imageDoc = imagesSnapshot.docs.find(doc =>
            doc.data().imageUrl === selectedNode.imageUrl
          );

          if (imageDoc) {
            targetJourneyDoc = journeyDoc;
            targetImageDoc = imageDoc;
            break;
          }
        }

        if (!targetJourneyDoc || !targetImageDoc) {
          throw new Error("Image not found in any journey");
        }

        // Get current response array
        const currentData = targetImageDoc.data();
        if (!currentData) {
          throw new Error("Image document data is undefined");
        }

        const originalResponse = currentData.response || [];
        if (!Array.isArray(originalResponse)) {
          throw new Error(`Invalid response format: ${JSON.stringify(originalResponse)}`);
        }

        // Create new response array by removing selected points and adding the merged point
        const selectedIndices = points.map((p, idx) => p.selected ? idx : -1).filter(idx => idx !== -1);
        const responseWithoutSelected = originalResponse.filter((_, idx) => !selectedIndices.includes(idx));

        // Create sanitized merged point
        const mergedPoint = {
          text: mergedText || "",
          type: selectedPoints[0].type || "other",
          kind: selectedPoints[0].type || "other"
        };

        // Only add laws if they exist
        if (allLaws.length > 0) {
          mergedPoint.laws = allLaws;
        }

        const newResponse = [...responseWithoutSelected, mergedPoint];

        console.log("Saving merged point data:", JSON.stringify(newResponse));

        // Update Firebase
        const imageRef = doc(targetJourneyDoc.ref, "images", targetImageDoc.id);
        await updateDoc(imageRef, {
          response: newResponse
        });

        // Update selected node state
        setSelectedNode(prev => ({
          ...prev,
          response: newResponse
        }));

        console.log("Points merged successfully in Firebase");
      } catch (error) {
        console.error("Error merging points in Firebase:", error);
        alert(`Failed to merge points in Firebase: ${error.message}`);
      } finally {
        setLoading(false);
      }
    }
  };

  /**
   * Deletes a point from both local state and Firebase.
   * If the point doesn't exist in Firebase yet (newly added), only removes from local state.
   * @param {number} index - The index of the point to delete
   */
  const handleDeletePoint = async (index) => {
    try {
      setLoading(true);

      // Remove from local state immediately for better UX
      const newPoints = points.filter((_, i) => i !== index);
      setPoints(newPoints);

      // If selectedNode exists, update Firebase as well
      if (selectedNode) {
        // Get reference to all user journeys
        const journeysRef = collection(db, "sessions", sessionId, "userJourneys");
        const journeysSnapshot = await getDocs(journeysRef);
        let targetJourneyDoc = null;
        let targetImageDoc = null;

        // Find the journey and image containing the selected node
        for (const journeyDoc of journeysSnapshot.docs) {
          const imagesRef = collection(journeyDoc.ref, "images");
          const imagesSnapshot = await getDocs(imagesRef);

          const imageDoc = imagesSnapshot.docs.find(doc =>
            doc.data().imageUrl === selectedNode.imageUrl
          );

          if (imageDoc) {
            targetJourneyDoc = journeyDoc;
            targetImageDoc = imageDoc;
            break;
          }
        }

        if (!targetJourneyDoc || !targetImageDoc) {
          throw new Error("Image not found in any journey");
        }

        // Get current response array from Firebase
        const currentData = targetImageDoc.data();
        if (!currentData) {
          throw new Error("Image document data is undefined");
        }

        const responseArray = currentData.response || [];
        if (!Array.isArray(responseArray)) {
          throw new Error(`Invalid response format: ${JSON.stringify(responseArray)}`);
        }

        // Check if the point exists in Firebase (index is within bounds)
        if (index < responseArray.length) {
          // Remove the point from the response array
          const updatedResponse = responseArray.filter((_, i) => i !== index);

          // Update Firebase
          const imageRef = doc(targetJourneyDoc.ref, "images", targetImageDoc.id);
          await updateDoc(imageRef, {
            response: updatedResponse
          });

          // Update selected node state
          setSelectedNode(prev => ({
            ...prev,
            response: updatedResponse
          }));

          toast.success('Point deleted successfully!');
        } else {
          // Point doesn't exist in Firebase yet (was just added locally)
          toast.info('Point removed from local state');
        }
      }

    } catch (error) {
      console.error("Error deleting point:", error);
      toast.error(`Failed to delete point: ${error.message}`);

      // Revert local state change on error
      setPoints(points);
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch laws list
  const fetchLawsList = async () => {
    await fetchLawsWithRanking();
  };

  // Function to fetch clauses for selected law
  const fetchClausesForLaw = async (lawId) => {
    setLoading(true);
    try {
      const Airtable = require('airtable');
      const base = new Airtable({ apiKey: process.env.REACT_APP_AIRTABLE_API_KEY }).base('apprwDgpLMtBvjfTQ');

      // First, let's get all records for this table
      const records = await base('tblhtouSOqKxrA7k0').select().all();

      // Log the first record to see its structure
      if (records.length > 0) {
        console.log('First record structure:', {
          id: records[0].id,
          fields: records[0].fields
        });
      }

      // Filter records that have the selected law ID in their Law field
      const filteredRecords = records.filter(record => {
        const lawField = record.fields.Law; // or whatever the actual field name is
        return Array.isArray(lawField) && lawField.includes(lawId);
      });

      const clauses = filteredRecords.map(record => ({
        id: record.id,
        clause: record.fields.Clause || record.fields.clause, // try both cases
        link: record.fields['Link to passage'] || record.fields.link
      }));

      console.log('Filtered clauses:', clauses);
      setClausesList(clauses);

      // Get jurisdiction for the selected law
      const selectedLaw = lawsList.find(law => law.id === lawId);
      if (selectedLaw && selectedLaw.jurisdiction) {
        setSelectedJurisdiction(selectedLaw.jurisdiction);
      } else {
        setSelectedJurisdiction('N/A');
      }

    } catch (error) {
      console.error('Error fetching clauses:', error);
      console.log('Error details:', {
        message: error.message,
        error: error.error,
        statusCode: error.statusCode,
        lawId: lawId
      });
    } finally {
      setLoading(false);
    }
  };

  // Add useEffect to fetch laws when the popup opens
  useEffect(() => {
    if (isLawsPopupOpen) {
      fetchLawsList();
    }
  }, [isLawsPopupOpen]);

  // Add useEffect to fetch dark patterns when component loads
  useEffect(() => {
    fetchDarkPatterns();
  }, []);

  // Function to save laws for a point
  const saveLawsForPoint = async () => {
    if (!selectedPointForLaws || !selectedNode) {
      console.log("No point or node selected");
      return;
    }

    try {
      // console.log("Starting save process...");
      // console.log("Selected point:", selectedPointForLaws);
      // console.log("Selected laws to save:", selectedLaws);

      const journeysRef = collection(db, "sessions", sessionId, "userJourneys");
      const journeysSnapshot = await getDocs(journeysRef);
      let targetJourneyDoc = null;
      let targetImageDoc = null;

      // Find the correct journey and image
      for (const journeyDoc of journeysSnapshot.docs) {
        const imagesRef = collection(journeyDoc.ref, "images");
        const imagesSnapshot = await getDocs(imagesRef);

        const imageDoc = imagesSnapshot.docs.find(doc =>
          doc.data().imageUrl === selectedNode.imageUrl
        );

        if (imageDoc) {
          targetJourneyDoc = journeyDoc;
          targetImageDoc = imageDoc;
          break;
        }
      }

      if (!targetJourneyDoc || !targetImageDoc) {
        throw new Error("Image not found in any journey");
      }

      // Get current response array
      const currentData = targetImageDoc.data();
      const responseArray = currentData.response || [];
      // console.log("Current response array:", responseArray);

      // Use the stored pointIndex to find the correct point
      const pointIndex = selectedPointForLaws.pointIndex;
      if (pointIndex === undefined || pointIndex >= points.length) {
        throw new Error("Invalid point index");
      }

      // Prepare laws data with userChoice, ensuring no undefined values
      const lawsToSave = selectedLaws
        .filter(law => law !== null && law !== undefined)
        .map(law => ({
          law_name: law.law || '',
          clause_name: law.clause || '',
          jurisdiction: law.jurisdiction || 'N/A',
          link: law.link || '',
          type: law.type || 'added',
          userChoice: law.userChoice === undefined ? true : law.userChoice
        }));

      // Get the current point from Firebase
      const currentPoint = responseArray[pointIndex] || {};

      // Only update the specific point in the response array
      const updatedResponseArray = [...responseArray];
      updatedResponseArray[pointIndex] = {
        ...currentPoint,
        text: points[pointIndex].text || '',
        type: points[pointIndex].type || 'outcome',
        kind: points[pointIndex].type || 'outcome', // For compatibility
        dark_pattern: points[pointIndex].dark_pattern || null,
        parent_type: points[pointIndex].parent_type || null,
        fairpatterns_url: points[pointIndex].fairpatterns_url || null,
        coordinates: points[pointIndex].coordinates || null, // Preserve coordinates
        priority: points[pointIndex].priority || 1, // Include priority
        laws: lawsToSave
      };

      // console.log("Updated point to save:", updatedResponseArray[pointIndex]);

      // Update Firebase
      const imageRef = doc(targetJourneyDoc.ref, "images", targetImageDoc.id);
      await updateDoc(imageRef, {
        response: updatedResponseArray
      });

      // console.log("Firebase update successful");

      // Update selected node with the fresh data
      setSelectedNode(prev => ({
        ...prev,
        response: updatedResponseArray
      }));

      // Update only the specific point in the points array
      const updatedPoints = [...points];
      updatedPoints[pointIndex] = {
        ...points[pointIndex],
        laws: lawsToSave
      };
      setPoints(updatedPoints);

      // Close popup and reset states
      setIsLawsPopupOpen(false);
      setSelectedPointForLaws(null);
      setSelectedLaws([]);

      alert('Laws saved successfully!');

    } catch (error) {
      // console.error("Error saving laws:", error);
      alert(`Failed to save laws: ${error.message}`);
    }
  };

  // Function to load saved laws when opening the popup
  const handleOpenLawsPopup = (point, index) => {
    // console.log("Opening laws popup for point:", point, "at index:", index);

    // Create a point object with index
    const pointWithIndex = {
      ...point,
      pointIndex: index
    };
    setSelectedPointForLaws(pointWithIndex);
    setLawsToRemove([]);

    // Load any previously saved laws for this point
    if (point.laws && Array.isArray(point.laws)) {
      // console.log("Found existing laws:", point.laws);
      setSelectedLaws(point.laws.map(law => ({
        id: `${law.law_name}-${law.clause_name}`,
        law: law.law_name,
        jurisdiction: law.jurisdiction || 'N/A',
        clause: law.clause_name,
        link: law.link,
        type: law.type || 'added',
        userChoice: law.userChoice !== undefined ? law.userChoice : true
      })));
    } else {
      // console.log("No existing laws found");
      setSelectedLaws([]);
    }

    setIsLawsPopupOpen(true);
  };

  // Modify the existing sortedLaws useMemo to include ranking
  const sortedLaws = React.useMemo(() => {
    let sortedItems = [...selectedLaws];

    // Sort by jurisdiction priority and ranking
    sortedItems.sort((a, b) => {
      // First sort by jurisdiction priority
      const priorityDiff = (JURISDICTION_PRIORITY[a.jurisdiction] || 999) -
        (JURISDICTION_PRIORITY[b.jurisdiction] || 999);

      if (priorityDiff !== 0) return priorityDiff;

      // Then sort by ranking (lower number first)
      const aLaw = lawsList.find(l => l.shortName === a.law);
      const bLaw = lawsList.find(l => l.shortName === b.law);

      return (aLaw?.ranking || 999) - (bLaw?.ranking || 999);
    });

    // Apply additional sorting if configured
    if (sortConfig.key && sortConfig.key !== 'ranking') {
      sortedItems.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return sortedItems;
  }, [selectedLaws, sortConfig, lawsList]);

  // Update the filteredLaws useMemo to properly filter by jurisdiction
  const filteredLaws = React.useMemo(() => {
    return sortedLaws.filter(law => {
      const searchText = filterText.toLowerCase();
      if (!searchText) return true; // If no filter is selected, show all laws

      // Compare jurisdiction (accounting for null/undefined values)
      const lawJurisdiction = (law.jurisdiction || 'N/A').toLowerCase();
      return lawJurisdiction === searchText.toLowerCase();
    });
  }, [sortedLaws, filterText]);

  // Modify the fetchLawsForPoint function to include loading state
  const fetchLawsForPoint = async () => {
    if (!selectedPointForLaws) {
      console.log("No point selected");
      return;
    }

    setIsFetchingLaws(true);
    try {
      const pointIndex = selectedPointForLaws.pointIndex;
      if (pointIndex === undefined || pointIndex >= points.length) {
        throw new Error("Invalid point index");
      }

      // Get the actual point object from the points array to access its coordinates
      const pointData = points[pointIndex];

      // console.log("Sending point data to API:", {
      //   point: selectedPointForLaws.text,
      //   coordinates: pointData.coordinates || null
      // });

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/get_relevent_regulatory_preventions`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            point: selectedPointForLaws.text,
            coordinates: pointData.coordinates || null
          })
        }
      );

      const result = await response.json();

      if (result.status === 'success' && result.data) {
        const { dark_pattern, laws, fair_patterns } = result.data;

        // Extract dark pattern name but don't modify the text
        const darkPatternName = dark_pattern?.name || null;

        // Format laws data
        const newLaws = laws.map(law => ({
          id: `${law.law_name}-${law.clause}`,
          law: law.law_name,
          jurisdiction: law.jurisdiction || 'N/A',
          clause: law.clause,
          link: law.link_to_passage || '',
          type: law.type || 'Extracted',
          userChoice: true
        }));

        // Get the first fair pattern URL if available
        const fairPatternsUrl = fair_patterns?.[0]?.fair_pattern_visualization?.[0]?.url || null;

        const existingLawKeys = new Set(
          selectedLaws.map(law => `${law.law}-${law.clause}`)
        );

        const uniqueNewLaws = newLaws.filter(
          law => !existingLawKeys.has(`${law.law}-${law.clause}`)
        );

        // Update only the specific point while preserving its coordinates and original text
        const updatedPoints = [...points];
        const updatedPoint = {
          ...points[pointIndex],
          // Keep original text unchanged
          text: selectedPointForLaws.text,
          dark_pattern: darkPatternName,
          parent_type: result.data.parent_type || null,
          fairpatterns_url: fairPatternsUrl, // Save fair pattern URL
          // Preserve the coordinates
          coordinates: pointData.coordinates,
          pointIndex: pointIndex
        };
        updatedPoints[pointIndex] = updatedPoint;
        setPoints(updatedPoints);

        setSelectedLaws([...selectedLaws, ...uniqueNewLaws]);
        setSelectedPointForLaws(updatedPoint);

        // If there's a fair pattern URL, update the image URL
        if (fairPatternsUrl) {
          setFairPatternsImageUrl(fairPatternsUrl);
        }

        if (uniqueNewLaws.length === 0) {
          alert('No new laws found for this point');
        } else {
          alert(`Added ${uniqueNewLaws.length} new laws`);
        }
      } else {
        throw new Error(result.message || 'Failed to fetch laws');
      }
    } catch (error) {
      console.error("Error fetching laws:", error);
      alert(`Failed to fetch laws: ${error.message}`);
    } finally {
      setIsFetchingLaws(false);
    }
  };

  // In your component, make sure this state is defined
  const [selectedJurisdiction, setSelectedJurisdiction] = useState('');

  // Removed redundant fetchCompleteNodeData effect to prevent data regression.
  // Full data fetching is now handled robustly in handleNodeSelection and handleNodeClick.


  // Add new state for fair patterns
  const [isFairPatternsPopupOpen, setIsFairPatternsPopupOpen] = useState(false);
  const [selectedPointForFairPatterns, setSelectedPointForFairPatterns] = useState(null);
  const [fairPatternsLoading, setFairPatternsLoading] = useState(false);
  const [fairPatternsImageUrl, setFairPatternsImageUrl] = useState(null);

  // Add function to fetch fair patterns
  const fetchFairPatterns = async (point) => {
    if (!point.dark_pattern) {
      alert('No dark pattern available for this point');
      return;
    }

    // First check if the point already has a fairpatterns_url
    if (point.fairpatterns_url) {
      setFairPatternsImageUrl(point.fairpatterns_url);
      return;
    }

    setFairPatternsLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/fetch_fairpatterns_for_dark_pattern`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            dark_pattern: point.dark_pattern
          })
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.status !== 'success' || !data.data) {
        throw new Error('Invalid response from server');
      }

      // Extract the image URL from the response
      const fairPatternsUrl = data.data;
      setFairPatternsImageUrl(fairPatternsUrl);

      // Update the point with fair patterns data in Firebase
      if (selectedNode) {
        const journeysRef = collection(db, "sessions", sessionId, "userJourneys");
        const journeysSnapshot = await getDocs(journeysRef);
        let targetJourneyDoc = null;
        let targetImageDoc = null;

        // Find the correct journey and image
        for (const journeyDoc of journeysSnapshot.docs) {
          const imagesRef = collection(journeyDoc.ref, "images");
          const imagesSnapshot = await getDocs(imagesRef);

          const imageDoc = imagesSnapshot.docs.find(doc =>
            doc.data().imageUrl === selectedNode.imageUrl
          );

          if (imageDoc) {
            targetJourneyDoc = journeyDoc;
            targetImageDoc = imageDoc;
            break;
          }
        }

        if (!targetJourneyDoc || !targetImageDoc) {
          throw new Error("Image not found in any journey");
        }

        // Get current response array
        const currentData = targetImageDoc.data();
        const responseArray = currentData.response || [];

        // Update the point with fair patterns data
        const updatedResponse = responseArray.map((p) => {
          if (p.text === point.text && p.dark_pattern === point.dark_pattern) {
            return {
              ...p,
              fairpatterns_url: fairPatternsUrl // Use consistent field name
            };
          }
          return p;
        });

        // Update Firebase
        const imageRef = doc(targetJourneyDoc.ref, "images", targetImageDoc.id);
        await updateDoc(imageRef, {
          response: updatedResponse
        });

        // Update local state
        setPoints(points.map((p) => {
          if (p.text === point.text && p.dark_pattern === point.dark_pattern) {
            return {
              ...p,
              fairpatterns_url: fairPatternsUrl
            };
          }
          return p;
        }));
      }

    } catch (error) {
      console.error("Error fetching fair patterns:", error);
      alert('Failed to fetch fair patterns: ' + error.message);
    } finally {
      setFairPatternsLoading(false);
    }
  };

  // Add the handlePriorityChange function before renderPoints
  const handlePriorityChange = async (index, newPriority) => {
    const updatedPoints = points.map((point, i) =>
      i === index ? { ...point, priority: newPriority } : point
    );
    setPoints(updatedPoints);

    // If the selected node exists, update Firebase
    if (selectedNode) {
      try {
        setLoading(true);
        // Get reference to all user journeys
        const journeysRef = collection(db, "sessions", sessionId, "userJourneys");
        const journeysSnapshot = await getDocs(journeysRef);
        let targetJourneyDoc = null;
        let targetImageDoc = null;

        // Find the journey and image containing the selected node
        for (const journeyDoc of journeysSnapshot.docs) {
          const imagesRef = collection(journeyDoc.ref, "images");
          const imagesSnapshot = await getDocs(imagesRef);

          const imageDoc = imagesSnapshot.docs.find(doc =>
            doc.data().imageUrl === selectedNode.imageUrl
          );

          if (imageDoc) {
            targetJourneyDoc = journeyDoc;
            targetImageDoc = imageDoc;
            break;
          }
        }

        if (!targetJourneyDoc || !targetImageDoc) {
          throw new Error("Image not found in any journey");
        }

        // Get current response array
        const currentData = targetImageDoc.data();
        if (!currentData) {
          throw new Error("Image document data is undefined");
        }

        const responseArray = currentData.response || [];
        if (!Array.isArray(responseArray)) {
          throw new Error(`Invalid response format: ${JSON.stringify(responseArray)}`);
        }

        // Update the priority for the specific point
        const updatedResponse = responseArray.map((point, i) => {
          if (i === index) {
            return {
              ...point,
              text: point.text || "",
              type: point.type || "outcome",
              kind: point.type || "outcome", // For compatibility
              priority: newPriority,
              // Preserve all other fields
              dark_pattern: point.dark_pattern || null,
              laws: point.laws || [],
              coordinates: point.coordinates || null,
              confirmedOutcome: point.confirmedOutcome || false,
              fairpatterns_url: point.fairpatterns_url || null
            };
          }
          return point;
        });

        // Update Firebase
        const imageRef = doc(targetJourneyDoc.ref, "images", targetImageDoc.id);
        await updateDoc(imageRef, {
          response: updatedResponse
        });

        // Update selected node state
        setSelectedNode(prev => ({
          ...prev,
          response: updatedResponse
        }));

        // console.log(`Point priority updated to ${newPriority} successfully`);
      } catch (error) {
        // console.error("Error updating point priority:", error, "Index:", index, "NewPriority:", newPriority);
        alert(`Failed to update point priority in Firebase: ${error.message}`);
      } finally {
        setLoading(false);
      }
    }
  };

  // Add functions to handle edit mode
  const handleStartEdit = (index) => {
    setEditingPointIndex(index);
    setTempEditData({
      priority: points[index].priority || 1,
      dark_pattern: points[index].dark_pattern || '',
      text: points[index].text || ''
    });
  };

  const handleCancelEdit = () => {
    setEditingPointIndex(null);
    setTempEditData({});
  };

  const handleConfirmEdit = async () => {
    if (editingPointIndex === null) return;

    try {
      setLoading(true);

      // Get reference to all user journeys
      const journeysRef = collection(db, "sessions", sessionId, "userJourneys");
      const journeysSnapshot = await getDocs(journeysRef);
      let targetJourneyDoc = null;
      let targetImageDoc = null;

      // Find the journey and image containing the selected node
      for (const journeyDoc of journeysSnapshot.docs) {
        const imagesRef = collection(journeyDoc.ref, "images");
        const imagesSnapshot = await getDocs(imagesRef);

        const imageDoc = imagesSnapshot.docs.find(doc =>
          doc.data().imageUrl === selectedNode.imageUrl
        );

        if (imageDoc) {
          targetJourneyDoc = journeyDoc;
          targetImageDoc = imageDoc;
          break;
        }
      }

      if (!targetJourneyDoc || !targetImageDoc) {
        throw new Error("Image not found in any journey");
      }

      // Get current response array from Firebase
      const currentData = targetImageDoc.data();
      if (!currentData) {
        throw new Error("Image document data is undefined");
      }

      const responseArray = currentData.response || [];
      if (!Array.isArray(responseArray)) {
        throw new Error(`Invalid response format: ${JSON.stringify(responseArray)}`);
      }

      let updatedResponse;

      // Check if this is a new point (index >= existing array length) or an existing point
      if (editingPointIndex >= responseArray.length) {
        // This is a NEW point - add it to the Firebase response array
        const newPoint = {
          text: tempEditData.text || "",
          type: points[editingPointIndex].type || "outcome",
          kind: points[editingPointIndex].type || "outcome", // For compatibility
          priority: tempEditData.priority !== undefined ? tempEditData.priority : (points[editingPointIndex].priority || 1),
          dark_pattern: tempEditData.dark_pattern || null,
          laws: [],
          coordinates: points[editingPointIndex].coordinates || null,
          confirmedOutcome: false
        };

        // Add the new point to the response array
        updatedResponse = [...responseArray, newPoint];
      } else {
        // This is an EXISTING point - update it
        updatedResponse = responseArray.map((point, i) => {
          if (i === editingPointIndex) {
            return {
              ...point,
              text: tempEditData.text !== undefined ? tempEditData.text : point.text,
              type: point.type || "outcome",
              kind: point.type || "outcome", // For compatibility
              priority: tempEditData.priority !== undefined ? tempEditData.priority : point.priority,
              dark_pattern: tempEditData.dark_pattern !== undefined ? tempEditData.dark_pattern : point.dark_pattern,
              // Preserve other fields
              laws: point.laws || [],
              coordinates: point.coordinates || null,
              confirmedOutcome: point.confirmedOutcome || false
            };
          }
          return point;
        });
      }

      // Update Firebase with the updated response array
      const imageRef = doc(targetJourneyDoc.ref, "images", targetImageDoc.id);
      await updateDoc(imageRef, {
        response: updatedResponse
      });

      // Update selected node state
      setSelectedNode(prev => ({
        ...prev,
        response: updatedResponse
      }));

      // Update local points state
      const updatedPoints = points.map((point, i) => {
        if (i === editingPointIndex) {
          return {
            ...point,
            text: tempEditData.text !== undefined ? tempEditData.text : point.text,
            priority: tempEditData.priority !== undefined ? tempEditData.priority : point.priority,
            dark_pattern: tempEditData.dark_pattern !== undefined ? tempEditData.dark_pattern : point.dark_pattern
          };
        }
        return point;
      });
      setPoints(updatedPoints);

      // Reset edit mode
      setEditingPointIndex(null);
      setTempEditData({});

      toast.success('Changes saved successfully!');

    } catch (error) {
      console.error("Error confirming edit:", error);
      toast.error(`Failed to save changes: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Add function to handle confirm outcome
  const handleConfirmOutcome = async (index) => {
    const updatedPoints = points.map((point, i) =>
      i === index ? { ...point, confirmedOutcome: true } : point
    );
    setPoints(updatedPoints);

    // Update Firebase
    if (selectedNode) {
      try {
        setLoading(true);
        const journeysRef = collection(db, "sessions", sessionId, "userJourneys");
        const journeysSnapshot = await getDocs(journeysRef);
        let targetJourneyDoc = null;
        let targetImageDoc = null;

        for (const journeyDoc of journeysSnapshot.docs) {
          const imagesRef = collection(journeyDoc.ref, "images");
          const imagesSnapshot = await getDocs(imagesRef);

          const imageDoc = imagesSnapshot.docs.find(doc =>
            doc.data().imageUrl === selectedNode.imageUrl
          );

          if (imageDoc) {
            targetJourneyDoc = journeyDoc;
            targetImageDoc = imageDoc;
            break;
          }
        }

        if (!targetJourneyDoc || !targetImageDoc) {
          throw new Error("Image not found in any journey");
        }

        const currentData = targetImageDoc.data();
        const responseArray = currentData.response || [];

        const updatedResponse = responseArray.map((point, i) => {
          if (i === index) {
            return {
              ...point,
              confirmedOutcome: true
            };
          }
          return point;
        });

        const imageRef = doc(targetJourneyDoc.ref, "images", targetImageDoc.id);
        await updateDoc(imageRef, {
          response: updatedResponse
        });

        setSelectedNode(prev => ({
          ...prev,
          response: updatedResponse
        }));

        // Save confirmed outcome to demo-images collection
        try {
          // Generate unique image name: sessionName + journeyName + pageName + 4-digit random number
          const randomNumber = Math.floor(1000 + Math.random() * 9000);
          const imageName = `${sessionName}_${selectedNode.journeyName}_${selectedNode.pageName || selectedNode.comprehensive_result?.categories?.webpage_category || 'Unknown'}_${randomNumber}`;

          // Prepare image data for demo-images collection
          const demoImageData = {
            image_name: imageName,
            sessionName: sessionName,
            sessionId: sessionId,
            journeyName: selectedNode.journeyName,
            pageName: selectedNode.pageName || selectedNode.comprehensive_result?.categories?.webpage_category || 'Unknown',
            imageUrl: selectedNode.imageUrl,
            url: selectedNode.url || selectedNode.Url,
            response: updatedResponse,
            confirmedOutcomes: updatedResponse.filter(point => point.confirmedOutcome),
            createdAt: new Date(),
            status: 'active'
          };

          // Add to demo-images collection
          await addDoc(collection(db, 'demo-images'), demoImageData);
          console.log('Confirmed outcome saved to demo-images collection');
        } catch (demoError) {
          console.error('Error saving to demo-images collection:', demoError);
          // Don't fail the main operation if demo save fails
        }

        toast.success('Outcome confirmed successfully!');
      } catch (error) {
        console.error("Error confirming outcome:", error);
        toast.error(`Failed to confirm outcome: ${error.message}`);
      } finally {
        setLoading(false);
      }
    }
  };

  // Add function to handle dark pattern changes
  const handleDarkPatternChange = async (index, newDarkPattern) => {
    const updatedPoints = points.map((point, i) =>
      i === index ? { ...point, dark_pattern: newDarkPattern } : point
    );
    setPoints(updatedPoints);

    // If the selected node exists, update Firebase
    if (selectedNode) {
      try {
        setLoading(true);
        // Get reference to all user journeys
        const journeysRef = collection(db, "sessions", sessionId, "userJourneys");
        const journeysSnapshot = await getDocs(journeysRef);
        let targetJourneyDoc = null;
        let targetImageDoc = null;

        // Find the journey and image containing the selected node
        for (const journeyDoc of journeysSnapshot.docs) {
          const imagesRef = collection(journeyDoc.ref, "images");
          const imagesSnapshot = await getDocs(imagesRef);

          const imageDoc = imagesSnapshot.docs.find(doc =>
            doc.data().imageUrl === selectedNode.imageUrl
          );

          if (imageDoc) {
            targetJourneyDoc = journeyDoc;
            targetImageDoc = imageDoc;
            break;
          }
        }

        if (!targetJourneyDoc || !targetImageDoc) {
          throw new Error("Image not found in any journey");
        }

        // Get current response array
        const currentData = targetImageDoc.data();
        if (!currentData) {
          throw new Error("Image document data is undefined");
        }

        const responseArray = currentData.response || [];
        if (!Array.isArray(responseArray)) {
          throw new Error(`Invalid response format: ${JSON.stringify(responseArray)}`);
        }

        // Initialize fair patterns data
        let fairPatternsData = null;

        // If a dark pattern is selected (not empty), fetch fair patterns
        if (newDarkPattern && newDarkPattern.trim()) {
          try {
            console.log(`Fetching fair patterns for dark pattern: ${newDarkPattern}`);

            const fairPatternsResponse = await fetch(
              `${process.env.REACT_APP_API_BASE_URL}/get_fair_patterns_by_dark_pattern`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  dark_pattern_name: newDarkPattern.trim(),
                  image_id: targetImageDoc.id
                }),
              }
            );

            if (fairPatternsResponse.ok) {
              const fairPatternsResult = await fairPatternsResponse.json();

              if (fairPatternsResult.status === 'success' && fairPatternsResult.data) {
                fairPatternsData = fairPatternsResult.data;
                console.log(`Successfully fetched ${fairPatternsData.fair_patterns_count} fair patterns`);
              } else {
                console.warn('Fair patterns API returned non-success status:', fairPatternsResult);
              }
            } else {
              console.error('Failed to fetch fair patterns:', fairPatternsResponse.status);
            }
          } catch (fairPatternsError) {
            console.error('Error fetching fair patterns:', fairPatternsError);
            // Continue with the update even if fair patterns fetch fails
          }
        }

        // Update the dark pattern and fair patterns for the specific point
        const updatedResponse = responseArray.map((point, i) => {
          if (i === index) {
            const updatedPoint = {
              ...point,
              text: point.text || "",
              type: point.type || "outcome",
              kind: point.type || "outcome", // For compatibility
              dark_pattern: newDarkPattern || null,
              // Preserve all other fields
              laws: point.laws || [],
              coordinates: point.coordinates || null,
              priority: point.priority || 1,
              confirmedOutcome: point.confirmedOutcome || false,
              fairpatterns_url: point.fairpatterns_url || null
            };

            // Add fair patterns data if available
            if (fairPatternsData) {
              updatedPoint.fair_patterns = fairPatternsData;
              // Also update the fairpatterns_url if there are fair pattern visualizations
              if (fairPatternsData.fair_patterns && fairPatternsData.fair_patterns.length > 0) {
                const firstPattern = fairPatternsData.fair_patterns[0];
                if (firstPattern.fair_pattern_visualization && firstPattern.fair_pattern_visualization.length > 0) {
                  updatedPoint.fairpatterns_url = firstPattern.fair_pattern_visualization[0].url;
                }
              }
            } else if (!newDarkPattern || !newDarkPattern.trim()) {
              // Clear fair patterns data if dark pattern is removed
              delete updatedPoint.fair_patterns;
              delete updatedPoint.fairpatterns_url;
            }

            return updatedPoint;
          }
          return point;
        });

        // Update Firebase
        const imageRef = doc(targetJourneyDoc.ref, "images", targetImageDoc.id);
        await updateDoc(imageRef, {
          response: updatedResponse
        });

        // Update selected node state
        setSelectedNode(prev => ({
          ...prev,
          response: updatedResponse
        }));

        // Update local points state with fair patterns data
        const updatedPointsWithFairPatterns = points.map((point, i) => {
          if (i === index) {
            const updatedPoint = { ...point, dark_pattern: newDarkPattern };
            if (fairPatternsData && fairPatternsData.fair_patterns && fairPatternsData.fair_patterns.length > 0) {
              const firstPattern = fairPatternsData.fair_patterns[0];
              if (firstPattern.fair_pattern_visualization && firstPattern.fair_pattern_visualization.length > 0) {
                updatedPoint.fairpatterns_url = firstPattern.fair_pattern_visualization[0].url;
              }
            }
            return updatedPoint;
          }
          return point;
        });
        setPoints(updatedPointsWithFairPatterns);

        console.log(`Point dark pattern updated to ${newDarkPattern} successfully${fairPatternsData ? ' with fair patterns' : ''}`);

        // Show success message with fair patterns info
        if (fairPatternsData && fairPatternsData.fair_patterns_count > 0) {
          toast.success(`Dark pattern updated! Found ${fairPatternsData.fair_patterns_count} fair pattern(s).`);
        } else if (newDarkPattern && newDarkPattern.trim()) {
          toast.info('Dark pattern updated, but no fair patterns found.');
        } else {
          toast.success('Dark pattern cleared.');
        }

      } catch (error) {
        console.error("Error updating point dark pattern:", error, "Index:", index, "NewDarkPattern:", newDarkPattern);
        toast.error(`Failed to update point dark pattern: ${error.message}`);
      } finally {
        setLoading(false);
      }
    }
  };

  // New function to render response cards matching updated Figma design
  const renderResponseCards = () => {
    return points
      .sort((a, b) => {
        const typeOrder = {
          'outcome': 1,
          'hallucination': 2,
          'doubtful': 3,
          'ignore': 4
        };
        return typeOrder[a.type] - typeOrder[b.type];
      })
      .map((point, index) => {
        const isEditing = editingPointIndex === index;

        return (
          <div
            key={index}
            style={{
              marginBottom: '16px',
              border: point.confirmedOutcome ? '2px solid #00b74a' : '1px solid #E8EAFF',
              borderRadius: '8px',
              backgroundColor: '#fff',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
            }}
          >
            {/* Card Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 16px',
              borderBottom: '1px solid #f0f0f0'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {/* Selection Checkbox */}
                <input
                  type="checkbox"
                  checked={point.selected || false}
                  onChange={(e) => {
                    const newPoints = [...points];
                    newPoints[index] = { ...point, selected: e.target.checked };
                    setPoints(newPoints);
                  }}
                  disabled={loading}
                  style={{
                    opacity: loading ? 0.7 : 1,
                    cursor: loading ? 'not-allowed' : 'pointer'
                  }}
                />

                {/* Priority Dropdown or Tag */}
                {isEditing ? (
                  <select
                    value={tempEditData.priority || point.priority || 1}
                    onChange={(e) => setTempEditData({ ...tempEditData, priority: parseInt(e.target.value) })}
                    disabled={loading}
                    style={{
                      padding: '4px 8px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '500',
                      border: '1px solid #ddd',
                      opacity: loading ? 0.7 : 1,
                      cursor: loading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <option value={1}>High priority</option>
                    <option value={2}>High</option>
                    <option value={3}>Medium priority</option>
                    <option value={4}>Low</option>
                    <option value={5}>Lowest</option>
                  </select>
                ) : (
                  <span style={{
                    backgroundColor: point.priority === 1 ? '#FF6B6B' :
                      point.priority === 2 ? '#FFA726' :
                        point.priority === 3 ? '#FFD93D' :
                          point.priority === 4 ? '#66BB6A' : '#E0E0E0',
                    color: point.priority <= 2 ? '#fff' : '#000',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '500'
                  }}>
                    {point.priority === 1 ? 'High priority' :
                      point.priority === 2 ? 'High' :
                        point.priority === 3 ? 'Medium priority' :
                          point.priority === 4 ? 'Low' : 'Lowest'}
                  </span>
                )}

                {/* Dark Pattern Dropdown or Tag */}
                {isEditing ? (
                  <select
                    value={tempEditData.dark_pattern || point.dark_pattern || ''}
                    onChange={(e) => setTempEditData({ ...tempEditData, dark_pattern: e.target.value || null })}
                    style={{
                      padding: '4px 8px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '500',
                      border: '1px solid #ddd',
                      minWidth: '150px',
                      opacity: (isDarkPatternsLoading || loading) ? 0.7 : 1,
                      cursor: (isDarkPatternsLoading || loading) ? 'not-allowed' : 'pointer'
                    }}
                    disabled={isDarkPatternsLoading || loading}
                  >
                    <option value="">Select type of dark pattern</option>
                    {darkPatterns.map(pattern => (
                      <option key={pattern.id} value={pattern.name}>
                        {pattern.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  point.dark_pattern && (
                    <span style={{
                      backgroundColor: '#F3E5F5',
                      color: '#7B1FA2',
                      padding: '4px 8px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}>
                      {point.dark_pattern}
                    </span>
                  )
                )}

                {/* Confirmed Status */}
                {point.confirmedOutcome && (
                  <span style={{
                    backgroundColor: '#E8F5E8',
                    color: '#2E7D32',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '500'
                  }}>
                    ✓ Confirmed
                  </span>
                )}
              </div>

              {/* Edit Button */}
              <button
                onClick={() => isEditing ? handleCancelEdit() : handleStartEdit(index)}
                disabled={loading}
                style={{
                  backgroundColor: "transparent",
                  border: "1px solid #000EAB",
                  color: loading ? "#ccc" : "#000EAB",
                  padding: "6px 12px",
                  borderRadius: "50%",
                  fontSize: '14px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: loading ? 0.7 : 1
                }}
                title={isEditing ? "Cancel edit" : "Edit outcome"}
              >
                {isEditing ? '✕' : '✏️'}
              </button>
            </div>

            {/* Card Body - Text */}
            <div style={{ padding: '16px' }}>
              {/* Dark Pattern Title */}
              {point.dark_pattern && !isEditing && (
                <div style={{
                  fontSize: '16px',
                  fontWeight: 'bold',
                  color: '#333',
                  marginBottom: '12px'
                }}>
                  {point.dark_pattern}
                </div>
              )}

              {isEditing ? (
                <textarea
                  value={tempEditData.text !== undefined ? tempEditData.text : point.text}
                  onChange={(e) => setTempEditData({ ...tempEditData, text: e.target.value })}
                  disabled={loading}
                  style={{
                    width: '100%',
                    minHeight: '80px',
                    border: '1px solid #E8EAFF',
                    borderRadius: '4px',
                    padding: '12px',
                    fontSize: '14px',
                    resize: 'vertical',
                    fontFamily: 'inherit',
                    opacity: loading ? 0.7 : 1,
                    cursor: loading ? 'not-allowed' : 'pointer'
                  }}
                  placeholder="Write a description"
                />
              ) : (
                <div style={{
                  minHeight: '60px',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  color: '#666'
                }}>
                  {point.text || "Write a description"}
                </div>
              )}
            </div>

            {/* Card Footer - Actions */}
            <div style={{
              padding: '12px 16px',
              borderTop: '1px solid #f0f0f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                <button
                  onClick={() => handleOpenLawsPopup(point, index)}
                  disabled={loading}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: loading ? '#ccc' : '#000EAB',
                    fontSize: '14px',
                    textDecoration: 'underline',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.7 : 1
                  }}
                >
                  ⚖️ View Laws
                </button>

                <button
                  onClick={() => {
                    // Check if recommendations exist in the point
                    if (point.recommendations && point.recommendations.length > 0) {
                      setSelectedPointForRecommendations(point);
                      setIsRecommendationsPopupOpen(true);
                    } else {
                      toast.info('No recommendations available for this point. Please select a dark pattern first.');
                    }
                  }}
                  disabled={loading}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: loading ? '#ccc' : '#000EAB',
                    fontSize: '14px',
                    textDecoration: 'underline',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.7 : 1
                  }}
                >
                  💡 Recommendations
                </button>

                <button
                  onClick={() => {
                    // Helper function to find fair pattern image URL
                    const findFairPatternImageUrl = (point) => {
                      // First check if fairpatterns_url exists directly
                      if (point.fairpatterns_url) {
                        return point.fairpatterns_url;
                      }

                      // Check in fair_patterns.top_5_dark_patterns for fair_pattern_image_url
                      if (point.fair_patterns && point.fair_patterns.top_5_dark_patterns) {
                        for (const darkPattern of point.fair_patterns.top_5_dark_patterns) {
                          if (darkPattern.fair_pattern_image_url) {
                            return darkPattern.fair_pattern_image_url;
                          }
                        }
                      }

                      // Check in fair_pattern_images array
                      if (point.fair_pattern_images && point.fair_pattern_images.length > 0) {
                        const firstImage = point.fair_pattern_images[0];
                        if (firstImage.url) {
                          return firstImage.url;
                        }
                      }

                      return null;
                    };

                    const imageUrl = findFairPatternImageUrl(point);
                    if (imageUrl) {
                      // Create a modified point with the found image URL
                      const pointWithImageUrl = { ...point, fairpatterns_url: imageUrl };
                      setSelectedPointForFairPatterns(pointWithImageUrl);
                      setIsFairPatternsPopupOpen(true);
                    } else {
                      toast.info('No fair pattern image available for this point. Please select a dark pattern first.');
                    }
                  }}
                  disabled={loading}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: loading ? '#ccc' : '#000EAB',
                    fontSize: '14px',
                    textDecoration: 'underline',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.7 : 1
                  }}
                >
                  ✨ Fair Pattern
                </button>
              </div>

              {isEditing ? (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={handleCancelEdit}
                    disabled={loading}
                    style={{
                      padding: '6px 16px',
                      backgroundColor: 'transparent',
                      color: loading ? '#ccc' : '#666',
                      border: '1px solid #ddd',
                      borderRadius: '16px',
                      fontSize: '12px',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      opacity: loading ? 0.7 : 1
                    }}
                  >
                    Cancel changes
                  </button>
                  <button
                    onClick={handleConfirmEdit}
                    disabled={loading}
                    style={{
                      padding: '6px 16px',
                      backgroundColor: loading ? '#ccc' : '#00b74a',
                      color: '#fff',
                      border: '1px solid #00b74a',
                      borderRadius: '16px',
                      fontSize: '12px',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      opacity: loading ? 0.7 : 1
                    }}
                  >
                    {loading ? 'Saving...' : 'Confirm changes'}
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => handleDeletePoint(index)}
                    disabled={loading}
                    style={{
                      padding: '6px 16px',
                      backgroundColor: 'transparent',
                      color: loading ? '#ccc' : '#DD0004',
                      border: '1px solid #DD0004',
                      borderRadius: '16px',
                      fontSize: '12px',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      opacity: loading ? 0.7 : 1
                    }}
                  >
                    Delete
                  </button>
                  <button
                    onClick={() => handleConfirmOutcome(index)}
                    disabled={point.confirmedOutcome || loading}
                    style={{
                      padding: '6px 16px',
                      backgroundColor: point.confirmedOutcome ? '#00b74a' : 'transparent',
                      color: point.confirmedOutcome ? '#fff' : (loading ? '#ccc' : '#00b74a'),
                      border: '1px solid #00b74a',
                      borderRadius: '16px',
                      fontSize: '12px',
                      cursor: (point.confirmedOutcome || loading) ? 'not-allowed' : 'pointer',
                      opacity: (point.confirmedOutcome || loading) ? 0.7 : 1
                    }}
                  >
                    {point.confirmedOutcome ? 'Confirmed' : (loading ? 'Processing...' : 'Confirm')}
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      });
  };

  // Add this state and function near the top of the component
  const [isRetrying, setIsRetrying] = useState(false);

  // Add this function with your other handler functions
  const handleRetryImageProcessing = async () => {
    if (isRetrying || !selectedNode) return;

    setIsRetrying(true);

    try {
      // Get the necessary parameters from selectedNode
      const { url, imageUrl: image_url, note } = selectedNode;

      // Find the journey containing this image
      let foundJourneyId = null;
      let targetJourneyDoc = null;
      let targetImageDoc = null;

      if (session && session.userJourneys) {
        // Get reference to userJourneys collection
        const journeysRef = collection(db, "sessions", sessionId, "userJourneys");
        const journeysSnapshot = await getDocs(journeysRef);

        // Find the journey and image document
        for (const journeyDoc of journeysSnapshot.docs) {
          const imagesRef = collection(journeyDoc.ref, "images");
          const imagesSnapshot = await getDocs(imagesRef);

          const imageDoc = imagesSnapshot.docs.find(doc =>
            doc.data().imageUrl === selectedNode.imageUrl
          );

          if (imageDoc) {
            targetJourneyDoc = journeyDoc;
            targetImageDoc = imageDoc;
            foundJourneyId = journeyDoc.id;
            break;
          }
        }
      }

      if (!foundJourneyId) {
        console.warn("Could not find journey ID for image, using default");
      }

      // First update the status to 'initial' in Firebase if we found the image document
      if (targetJourneyDoc && targetImageDoc) {
        const imageRef = doc(targetJourneyDoc.ref, "images", targetImageDoc.id);
        await updateDoc(imageRef, {
          status: 'initial'
        });
        console.log("Updated image status to 'initial'");
      } else {
        console.warn("Could not find image document to update status");
      }

      // Call the endpoint in the background
      fetch(`${process.env.REACT_APP_API_BASE_URL}/get_image_metadata`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          image_url: image_url || selectedNode.imageUrl,
          url: url || selectedNode.url,
          note: note || selectedNode.note,
          session_id: sessionId,
          journey_id: foundJourneyId || "unknown",
          image_doc_id: targetImageDoc.id
        }),
      });

      // Show a notification or feedback message
      toast.info('Refreshing image analysis in the background');

      // We don't await the result since this should run in the background
      setTimeout(() => {
        setIsRetrying(false);
      }, 2000);
    } catch (error) {
      console.error('Error retrying image processing:', error);
      setIsRetrying(false);
      toast.error('Failed to refresh image analysis');
    }
  };

  return (
    <div style={{ display: "flex", height: "100vh", flexDirection: "column" }}>
      {/* Header with Session Name */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '15px 20px 8px 20px',
        backgroundColor: '#f8f9fa'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {/* Eye Icon */}
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ color: '#666' }}
          >
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
            <circle cx="12" cy="12" r="3"></circle>
          </svg>
          <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '600' }}>
            {sessionName || "Loading..."}
          </h2>
        </div>

        <button
          onClick={() => window.location.reload()}
          className="btn btn-outline-primary"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            padding: '8px 16px',
            fontSize: '14px'
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            style={{
              marginRight: '5px',
              transform: 'rotate(90deg)'
            }}
          >
            <path
              fill="currentColor"
              d="M8 3a5 5 0 0 0-5 5H1l4 4 4-4H7a3 3 0 1 1 3 3V9a5 5 0 0 0-2-6z"
            />
          </svg>
          Refresh
        </button>
      </div>

      {/* Tab Navigation - Below Session Name, Left Aligned */}
      <div style={{
        display: 'flex',
        justifyContent: 'flex-start',
        padding: '0 10px 12px 20px',
        borderBottom: '1px solid #ddd',
        backgroundColor: '#f8f9fa'
      }}>
        <div style={{ display: 'flex', gap: '30px', alignItems: 'center' }}>
          <button
            onClick={() => {
              setActiveTab('all-screens');
              setViewMode('flow');
            }}
            style={{
              padding: '0',
              backgroundColor: 'transparent',
              color: activeTab === 'all-screens' ? '#000' : '#666',
              border: 'none',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: activeTab === 'all-screens' ? '600' : '400',
              borderBottom: activeTab === 'all-screens' ? '3px solid #000' : '3px solid transparent',
              paddingBottom: '8px',
              transition: 'all 0.2s ease'
            }}
          >
            All screens
          </button>
          <button
            onClick={() => {
              setActiveTab('detailed-review');
              setViewMode('split');
            }}
            style={{
              padding: '0',
              backgroundColor: 'transparent',
              color: activeTab === 'detailed-review' ? '#000' : '#666',
              border: 'none',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: activeTab === 'detailed-review' ? '600' : '400',
              borderBottom: activeTab === 'detailed-review' ? '3px solid #000' : '3px solid transparent',
              paddingBottom: '8px',
              transition: 'all 0.2s ease'
            }}
          >
            Detailed review
          </button>
        </div>
      </div>

      {/* Content Area */}
      <div style={{ flex: 1, display: "flex" }}>
        {activeTab === 'all-screens' ? (
          // All Screens Flow View
          <div style={{ flex: 1, position: "relative" }}>
            {!session ? (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                fontSize: '18px',
                color: '#666'
              }}>
                Loading session data...
              </div>
            ) : !session.userJourneys || session.userJourneys.length === 0 ? (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                fontSize: '18px',
                color: '#666',
                gap: '10px'
              }}>
                <p>No user journeys found in this session.</p>
                <button
                  onClick={() => window.location.reload()}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#4F46E5',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Refresh Session
                </button>
              </div>
            ) : (
              <ReactFlow
                nodes={combinedNodes}
                edges={combinedEdges}
                onNodeClick={handleNodeClick}
                nodesDraggable={true}
                connectionLineType={ConnectionLineType.SmoothStep}
                style={{ height: "100%" }}
                fitView
                attributionPosition="bottom-right"
                minZoom={0.1}
                maxZoom={4}
                defaultViewport={{ x: 0, y: 0, zoom: 0.8 }}
              >
                <Background />
                <Controls />
              </ReactFlow>
            )}
          </div>
        ) : (
          // Detailed Review (Split Screen View)
          <div
            style={{ display: "flex", height: "100%", position: "relative" }}
            data-resizable-container
          >
            {/* Left side - Resizable - Image and Properties */}
            <div style={{
              width: `${leftWidth}%`,
              display: "flex",
              flexDirection: "column",
              borderRight: "1px solid #ddd"
            }}>
              {/* Header with Journey and Page Info */}
              <div style={{
                padding: '20px',
                borderBottom: '1px solid #ddd',
                backgroundColor: '#fff'
              }}>
                <div style={{ marginBottom: '16px' }}>
                  <h3 style={{
                    margin: '0 0 4px 0',
                    fontSize: '20px',
                    fontWeight: '600',
                    color: '#333'
                  }}>
                    {selectedNode?.journeyName || "No Journey Selected"}
                  </h3>
                  <p style={{
                    margin: '0',
                    fontSize: '16px',
                    color: '#666',
                    fontWeight: '500'
                  }}>
                    {selectedNode?.comprehensive_result?.categories?.webpage_category || selectedNode?.pageName || "Unknown Page"}
                  </p>
                </div>

                {/* Navigation */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '14px', color: '#666' }}>
                    Image {currentImageIndex + 1} of {allImages.length}
                  </span>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={handlePreviousImage}
                      disabled={currentImageIndex === 0}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: currentImageIndex === 0 ? '#f5f5f5' : '#000EAB',
                        color: currentImageIndex === 0 ? '#999' : '#fff',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '12px',
                        cursor: currentImageIndex === 0 ? 'not-allowed' : 'pointer'
                      }}
                    >
                      ← Previous
                    </button>
                    <button
                      onClick={handleNextImage}
                      disabled={currentImageIndex === allImages.length - 1}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: currentImageIndex === allImages.length - 1 ? '#f5f5f5' : '#000EAB',
                        color: currentImageIndex === allImages.length - 1 ? '#999' : '#fff',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '12px',
                        cursor: currentImageIndex === allImages.length - 1 ? 'not-allowed' : 'pointer'
                      }}
                    >
                      Next →
                    </button>
                  </div>
                </div>
              </div>

              {/* Image Preview */}
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  backgroundColor: '#f8f9fa',
                  position: 'relative',
                  minHeight: '300px'
                }}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              >
                {selectedNode?.imageUrl ? (
                  <img
                    src={selectedNode.imageUrl}
                    alt="Screenshot"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      transform: `scale(${zoomLevel}) translate(${imagePosition.x}px, ${imagePosition.y}px)`,
                      transformOrigin: 'center',
                      cursor: isDragging ? 'grabbing' : 'grab',
                      userSelect: 'none'
                    }}
                    onMouseDown={handleMouseDown}
                    onClick={() => setIsModalOpen(true)}
                  />
                ) : (
                  <div style={{
                    padding: '40px',
                    textAlign: 'center',
                    color: '#6c757d'
                  }}>
                    <p>No image selected</p>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              {selectedNode && (
                <div style={{
                  padding: '20px',
                  borderTop: '1px solid #ddd',
                  backgroundColor: '#fff'
                }}>
                  <div style={{
                    display: 'flex',
                    gap: '16px',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <button
                      onClick={() => {
                        // Prepare navigation data before opening popup
                        const journey = (session.userJourneys || []).find(
                          (j) => j.journeyName === selectedNode.journeyName
                        );

                        let navigationData = null;
                        if (journey) {
                          if (journey.navigationData) {
                            navigationData = journey.navigationData;
                          } else if (journey["navigation data"]) {
                            navigationData = journey["navigation data"];
                          } else if (journey.userJourney && journey.userJourney.navigationData) {
                            navigationData = journey.userJourney.navigationData;
                          } else if (Array.isArray(journey)) {
                            navigationData = journey;
                          }
                        }

                        if (navigationData && Array.isArray(navigationData)) {
                          setSelectedJourneyNavigation(navigationData);
                        } else {
                          setSelectedJourneyNavigation(null);
                        }

                        setIsPropertiesPopupOpen(true);
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#000EAB",
                        fontSize: '14px',
                        textDecoration: "underline",
                        cursor: "pointer",
                      }}
                    >
                      📄 Image Properties & Journey
                    </button>

                    <button
                      onClick={() => setIsModalOpen(true)}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#000EAB",
                        fontSize: '14px',
                        textDecoration: "underline",
                        cursor: "pointer",
                      }}
                    >
                      🔍 Full Screen View
                    </button>

                    <button
                      onClick={() => setIsRepresentativePopupOpen(true)}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#000EAB",
                        fontSize: '14px',
                        textDecoration: "underline",
                        cursor: "pointer",
                      }}
                    >
                      ✨ Representative Fair Image
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Resizable Divider */}
            <div
              style={{
                width: '4px',
                backgroundColor: isDraggingDivider ? '#000EAB' : '#ddd',
                cursor: 'col-resize',
                flexShrink: 0,
                transition: isDraggingDivider ? 'none' : 'background-color 0.2s'
              }}
              onMouseDown={handleDividerMouseDown}
            />

            {/* Right side - Resizable - Responses and Feedback */}
            <div style={{
              width: `${100 - leftWidth}%`,
              display: "flex",
              flexDirection: "column"
            }}>
              {selectedNode && (
                <div style={{
                  padding: "20px",
                  overflowY: "auto",
                  maxHeight: "100vh",
                  flex: 1,
                  backgroundColor: '#fff'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '20px'
                  }}>
                    <h3 style={{
                      margin: 0,
                      fontSize: '20px',
                      fontWeight: '600'
                    }}>
                      Outcomes
                    </h3>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={handleAddPoint}
                        style={{
                          backgroundColor: "transparent",
                          border: "1px solid #000EAB",
                          color: "#000EAB",
                          padding: "8px 12px",
                          borderRadius: "50%",
                          fontSize: '16px',
                          cursor: 'pointer',
                          width: '36px',
                          height: '36px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        title="Add new outcome"
                      >
                        +
                      </button>
                      <button
                        onClick={handleMergePoints}
                        disabled={points.filter(p => p.selected).length < 2}
                        style={{
                          backgroundColor: "transparent",
                          border: `1px solid ${points.filter(p => p.selected).length >= 2 ? "#000EAB" : "#ccc"}`,
                          color: points.filter(p => p.selected).length >= 2 ? "#000EAB" : "#ccc",
                          padding: "8px 12px",
                          borderRadius: "50%",
                          fontSize: '14px',
                          cursor: points.filter(p => p.selected).length >= 2 ? 'pointer' : 'not-allowed',
                          width: '36px',
                          height: '36px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        title="Merge selected outcomes"
                      >
                        {'><'}
                      </button>
                    </div>
                  </div>

                  {/* Response Cards */}
                  {!selectedNode.outcome ? (
                    <div style={{
                      textAlign: 'center',
                      padding: '40px 20px',
                      color: '#666'
                    }}>
                      <p style={{ marginBottom: '16px' }}>No responses available yet.</p>
                      <div className="d-flex align-items-center justify-content-center gap-2">
                        <button
                          onClick={handleProcessImage}
                          disabled={isProcessing}
                          style={{
                            backgroundColor: "#000EAB",
                            border: "none",
                            color: "#fff",
                            padding: "8px 16px",
                            borderRadius: "4px",
                            cursor: 'pointer'
                          }}
                        >
                          {isProcessing ? "Processing..." : "Get Image Details"}
                        </button>
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip id="tooltip-retry">Refresh image analysis</Tooltip>}
                        >
                          <button
                            className="retry-button"
                            onClick={handleRetryImageProcessing}
                            disabled={isRetrying}
                            style={{
                              background: 'none',
                              border: 'none',
                              fontSize: '1.5rem',
                              cursor: 'pointer'
                            }}
                          >
                            {isRetrying ? '🔄' : '🔄'}
                          </button>
                        </OverlayTrigger>
                      </div>
                    </div>
                  ) : (
                    <div>
                      {/* Response Cards */}
                      <div style={{ marginBottom: '24px' }}>
                        {renderResponseCards()}
                      </div>

                      {/* Internal Feedback Section */}
                      <div style={{
                        borderTop: '1px solid #ddd',
                        paddingTop: '20px'
                      }}>
                        <h4 style={{
                          margin: '0 0 16px 0',
                          fontSize: '16px',
                          fontWeight: '600'
                        }}>
                          Internal feedback
                        </h4>

                        <textarea
                          value={feedbackText}
                          onChange={(e) => setFeedbackText(e.target.value)}
                          style={{
                            width: "100%",
                            minHeight: "100px",
                            padding: "12px",
                            borderRadius: "4px",
                            border: "1px solid #E8EAFF",
                            resize: "vertical",
                            fontSize: '14px',
                            marginBottom: '12px'
                          }}
                          placeholder="Add your internal feedback here..."
                        />

                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={handleSavePointsAndFeedback}
                            style={{
                              backgroundColor: "#000EAB",
                              border: "none",
                              color: "#fff",
                              padding: "8px 16px",
                              borderRadius: "20px",
                              fontSize: '14px',
                              cursor: "pointer"
                            }}
                          >
                            Save All
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>


      )

      {/* XAI Popup */}
      {
        isXAIPopupOpen && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1000,
            }}
            onClick={() => setIsXAIPopupOpen(false)}
          >
            <div
              style={{
                width: '95%',
                height: '95%',
                backgroundColor: '#fff',
                borderRadius: '10px',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                padding: '20px',
                position: 'relative',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 style={{ textAlign: 'center', marginBottom: '10px' }}>
                Explainable AI Flow {selectedXAIPoint ? ` - ${selectedXAIPoint.type.toUpperCase()}` : ''}
              </h3>

              {selectedXAIPoint && (
                <div style={{
                  margin: '0 0 20px 0',
                  padding: '10px',
                  backgroundColor: '#f0f8ff',
                  borderRadius: '5px',
                  border: '1px solid #cce5ff'
                }}>
                  {/* <h5>Selected Point</h5>
                <p style={{ 
                  margin: '5px 0',
                  fontWeight: 'bold',
                  color: selectedXAIPoint.type === 'outcome' ? 'green' : 
                         selectedXAIPoint.type === 'hallucination' ? 'red' : 'orange'
                }}>
                  Type: {selectedXAIPoint.type.toUpperCase()}
                </p> */}
                  <p style={{ margin: '5px 0' }}>{selectedXAIPoint.text}</p>
                </div>
              )}

              <div style={{ flex: 1, border: '1px solid #ddd', borderRadius: '5px', overflow: 'hidden' }}>
                <ReactFlow
                  nodes={renderXAIFlowData().nodes}
                  edges={renderXAIFlowData().edges}
                  nodesDraggable={true}
                  nodesFocusable={true}
                  connectionLineType="smoothstep"
                  fitView
                  attributionPosition="bottom-right"
                >
                  <Background />
                  <Controls />
                  <MiniMap />
                </ReactFlow>
              </div>

              {/* Flow Legend */}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '15px' }}>
                <div>
                  <h5>Flow Legend</h5>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <div style={{
                        width: '12px',
                        height: '12px',
                        backgroundColor: '#0077b6',
                        borderRadius: '3px'
                      }}></div>
                      <span>Present in Front path</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <div style={{
                        width: '12px',
                        height: '12px',
                        backgroundColor: '#ffc107',
                        borderRadius: '3px'
                      }}></div>
                      <span>Present in Back path only</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <div style={{
                        width: '12px',
                        height: '12px',
                        backgroundColor: '#00b74a',
                        borderRadius: '3px'
                      }}></div>
                      <span>Present in both Front and Back paths</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <div style={{
                        width: '12px',
                        height: '12px',
                        backgroundColor: '#ffebee',
                        border: '2px solid #ef5350',
                        borderRadius: '3px'
                      }}></div>
                      <span>Failed validation</span>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => setIsXAIPopupOpen(false)}
                  style={{
                    padding: '8px 15px',
                    background: '#ff4d4f',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer',
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Laws Management Popup */}
      {
        isLawsPopupOpen && (
          <div
            onClick={() => {
              setIsLawsPopupOpen(false);
              setSelectedPointForLaws(null);
              setSelectedLaws([]);
            }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1000,
              cursor: 'pointer'
            }}
          >
            <div
              style={{
                backgroundColor: 'white',
                padding: '20px',
                borderRadius: '8px',
                width: '80%',
                maxHeight: '80vh',
                overflow: 'auto',
                cursor: 'default'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header section with dark pattern type and updated buttons */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px'
              }}>
                <div>
                  <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: '600', color: '#333' }}>
                    Laws Management
                  </h3>
                  {selectedPointForLaws?.dark_pattern && (
                    <div style={{
                      fontSize: '16px',
                      fontWeight: '500',
                      color: '#7B1FA2',
                      backgroundColor: '#F3E5F5',
                      padding: '8px 16px',
                      borderRadius: '20px',
                      display: 'inline-block'
                    }}>
                      Dark Pattern: {selectedPointForLaws.dark_pattern}
                    </div>
                  )}
                </div>

                {/* Laws Management Popup Buttons - Only plus, refresh, and save */}
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <button
                    onClick={() => setIsAddingLaw(true)}
                    style={{
                      backgroundColor: "#000EAB",
                      border: "none",
                      color: "#fff",
                      padding: "10px",
                      borderRadius: "50%",
                      cursor: "pointer",
                      width: '40px',
                      height: '40px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '18px',
                      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                    }}
                    title="Add new law"
                  >
                    ➕
                  </button>

                  <button
                    onClick={fetchLawsForPoint}
                    disabled={isFetchingLaws}
                    style={{
                      backgroundColor: "transparent",
                      border: "1px solid #000EAB",
                      color: "#000EAB",
                      padding: "10px",
                      borderRadius: "50%",
                      cursor: isFetchingLaws ? "not-allowed" : "pointer",
                      width: '40px',
                      height: '40px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '16px',
                      opacity: isFetchingLaws ? 0.6 : 1
                    }}
                    title="Fetch laws for this point"
                  >
                    {isFetchingLaws ? '🔄' : '🔄'}
                  </button>

                  <button
                    onClick={saveLawsForPoint}
                    style={{
                      backgroundColor: "#000EAB",
                      border: "none",
                      color: "#fff",
                      padding: "8px 20px",
                      borderRadius: "20px",
                      cursor: "pointer",
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    Save
                  </button>
                </div>
              </div>

              {/* Close button positioned at top right */}
              <button
                onClick={() => {
                  setIsLawsPopupOpen(false);
                  setSelectedPointForLaws(null);
                  setSelectedLaws([]);
                }}
                style={{
                  position: 'absolute',
                  top: '20px',
                  right: '20px',
                  backgroundColor: "transparent",
                  border: "1px solid #000EAB",
                  color: "#000EAB",
                  padding: "8px 12px",
                  borderRadius: "50%",
                  cursor: "pointer",
                  fontSize: '16px',
                  width: '36px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Close"
              >
                ✕
              </button>

              {/* Add Law Form */}
              {isAddingLaw && (
                <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                  <div style={{ marginBottom: '15px' }}>
                    <label>Select Law:</label>
                    <select
                      className="form-control"
                      value={selectedLawId}
                      onChange={(e) => {
                        setSelectedLawId(e.target.value);
                        setSelectedClause('');
                        if (e.target.value) {
                          fetchClausesForLaw(e.target.value);
                        }
                      }}
                    >
                      <option value="">Select a law...</option>
                      {lawsList.map(law => (
                        <option key={law.id} value={law.id}>
                          {law.shortName}
                        </option>
                      ))}
                    </select>
                  </div>

                  {selectedLawId && (
                    <>
                      <div style={{ marginBottom: '15px' }}>
                        <label>Jurisdiction:</label>
                        <input
                          type="text"
                          className="form-control"
                          value={selectedJurisdiction}
                          onChange={(e) => setSelectedJurisdiction(e.target.value)}
                          placeholder="Jurisdiction information"
                        />
                      </div>

                      <div style={{ marginBottom: '15px' }}>
                        <label>Select Clause:</label>
                        <select
                          className="form-control"
                          value={selectedClause}
                          onChange={(e) => {
                            const clauseId = e.target.value;
                            setSelectedClause(clauseId);

                            // Get link from the clausesList
                            const selectedClauseData = clausesList.find(c => c.id === clauseId);
                            if (selectedClauseData && selectedClauseData.link_to_passage) {
                              setSelectedLink(selectedClauseData.link_to_passage);
                            } else {
                              setSelectedLink('');
                            }
                          }}
                        >
                          <option value="">Select a clause...</option>
                          {clausesList.map(clause => (
                            <option key={clause.id} value={clause.id}>
                              {clause.clause}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div style={{ marginBottom: '15px' }}>
                        <label>Link to Passage:</label>
                        <input
                          type="text"
                          className="form-control"
                          value={selectedLink}
                          onChange={(e) => setSelectedLink(e.target.value)}
                          placeholder="Link will be auto-populated from Airtable"
                          readOnly={true}
                        />
                      </div>
                    </>
                  )}

                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                    <button
                      className="btn btn-success"
                      disabled={!selectedLawId || !selectedClause}
                      onClick={() => {
                        const law = lawsList.find(l => l.id === selectedLawId);
                        const clause = clausesList.find(c => c.id === selectedClause);
                        if (law && clause) {
                          setSelectedLaws([...selectedLaws, {
                            id: `${law.id}-${clause.id}`,
                            law: law.shortName,
                            jurisdiction: selectedJurisdiction,
                            clause: clause.clause,
                            link: selectedLink || clause.link || '',
                            type: 'added',
                            userChoice: true
                          }]);
                        }
                        setIsAddingLaw(false);
                        setSelectedLawId('');
                        setSelectedClause('');
                        setSelectedLink('');
                        setSelectedJurisdiction('');
                      }}
                    >
                      Add
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={() => {
                        setIsAddingLaw(false);
                        setSelectedLawId('');
                        setSelectedClause('');
                        setSelectedLink('');
                        setSelectedJurisdiction('');
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Informational text above the table */}
              <div style={{
                fontSize: '14px',
                color: '#6c757d',
                backgroundColor: '#f8f9fa',
                padding: '12px 16px',
                borderRadius: '8px',
                border: '1px solid #e9ecef',
                marginBottom: '20px'
              }}>
                <strong>Instructions:</strong> Select the laws you want to include in your report. Bold text indicates selected items. Use the eye icon to view passages and the delete icon to remove unwanted laws.
              </div>

              {/* Filter section with updated styling */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                marginBottom: '20px',
                padding: '16px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #e9ecef'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '14px', fontWeight: '500', color: '#495057' }}>🔍 Filter by Jurisdiction:</span>
                  <select
                    value={filterText}
                    onChange={(e) => setFilterText(e.target.value)}
                    style={{
                      padding: '8px 12px',
                      borderRadius: '6px',
                      border: '1px solid #ced4da',
                      fontSize: '14px',
                      backgroundColor: '#fff',
                      minWidth: '150px'
                    }}
                  >
                    <option value="">All Jurisdictions</option>
                    {Array.from(new Set(selectedLaws.map(law => law.jurisdiction || 'N/A')))
                      .sort()
                      .map(jurisdiction => (
                        <option key={jurisdiction} value={jurisdiction}>
                          {jurisdiction}
                        </option>
                      ))}
                  </select>
                </div>

                {filterText && (
                  <button
                    onClick={() => setFilterText('')}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: 'transparent',
                      border: '1px solid #6c757d',
                      color: '#6c757d',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    Clear Filter ❌
                  </button>
                )}

                {/* Remove Selected button - only visible when laws are selected */}
                {selectedLaws.some(law => law.userChoice) && (
                  <button
                    onClick={() => {
                      const updatedLaws = selectedLaws.filter(law => !law.userChoice);
                      setSelectedLaws(updatedLaws);
                    }}
                    style={{
                      backgroundColor: "transparent",
                      border: "1px solid #DD0004",
                      color: "#DD0004",
                      padding: "8px 16px",
                      borderRadius: "20px",
                      cursor: "pointer",
                      fontSize: '14px',
                      marginLeft: '16px'
                    }}
                  >
                    Remove Selected
                  </button>
                )}
              </div>

              {/* Updated table headers matching Figma design */}
              <table className="table" style={{ borderCollapse: 'separate', borderSpacing: '0', width: '100%' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                    <th style={{
                      width: '40px',
                      padding: '12px 8px',
                      textAlign: 'center',
                      borderBottom: '2px solid #dee2e6',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#495057'
                    }}>
                      <input
                        type="checkbox"
                        onChange={(e) => {
                          const newCheckedState = e.target.checked;
                          setSelectedLaws(selectedLaws.map(law => ({
                            ...law,
                            userChoice: newCheckedState
                          })));
                        }}
                        style={{ transform: 'scale(1.2)' }}
                      />
                    </th>
                    <th
                      onClick={() => {
                        setSortConfig({
                          key: 'law',
                          direction: sortConfig.key === 'law' && sortConfig.direction === 'asc' ? 'desc' : 'asc'
                        });
                      }}
                      style={{
                        cursor: 'pointer',
                        width: '20%',
                        padding: '12px 8px',
                        textAlign: 'left',
                        borderBottom: '2px solid #dee2e6',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#495057',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      Law {sortConfig.key === 'law' && (sortConfig.direction === 'asc' ? '⬆️' : '⬇️')}
                    </th>
                    <th
                      onClick={() => {
                        setSortConfig({
                          key: 'jurisdiction',
                          direction: sortConfig.key === 'jurisdiction' && sortConfig.direction === 'asc' ? 'desc' : 'asc'
                        });
                      }}
                      style={{
                        cursor: 'pointer',
                        width: '15%',
                        padding: '12px 8px',
                        textAlign: 'left',
                        borderBottom: '2px solid #dee2e6',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#495057',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      Jurisdiction {sortConfig.key === 'jurisdiction' && (sortConfig.direction === 'asc' ? '⬆️' : '⬇️')}
                    </th>
                    <th
                      onClick={() => {
                        setSortConfig({
                          key: 'clause',
                          direction: sortConfig.key === 'clause' && sortConfig.direction === 'asc' ? 'desc' : 'asc'
                        });
                      }}
                      style={{
                        cursor: 'pointer',
                        width: '35%',
                        padding: '12px 8px',
                        textAlign: 'left',
                        borderBottom: '2px solid #dee2e6',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#495057',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      Instructions {sortConfig.key === 'clause' && (sortConfig.direction === 'asc' ? '⬆️' : '⬇️')}
                    </th>
                    <th style={{
                      width: '15%',
                      padding: '12px 8px',
                      textAlign: 'left',
                      borderBottom: '2px solid #dee2e6',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#495057',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLaws.map((law, index) => (
                    <tr key={index} style={{
                      borderBottom: '1px solid #e9ecef',
                      backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa'
                    }}>
                      <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={law.userChoice || false}
                          onChange={(e) => {
                            const updatedLaws = selectedLaws.map((l) =>
                              l.id === law.id ? { ...l, userChoice: e.target.checked } : l
                            );
                            setSelectedLaws(updatedLaws);
                          }}
                          style={{ transform: 'scale(1.2)' }}
                        />
                      </td>
                      <td style={{
                        padding: '12px 8px',
                        fontWeight: law.userChoice ? 'bold' : 'normal',
                        color: law.userChoice ? '#000' : '#495057'
                      }}>
                        {law.law}
                      </td>
                      <td style={{
                        padding: '12px 8px',
                        fontWeight: law.userChoice ? 'bold' : 'normal',
                        color: law.userChoice ? '#000' : '#495057'
                      }}>
                        {law.jurisdiction || 'N/A'}
                      </td>
                      <td style={{
                        padding: '12px 8px',
                        fontWeight: law.userChoice ? 'bold' : 'normal',
                        color: law.userChoice ? '#000' : '#495057'
                      }}>
                        {law.clause}
                      </td>
                      <td style={{ padding: '12px 8px' }}>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          {/* SVG Eye icon for View Passage */}
                          {law.link ? (
                            <button
                              onClick={() => window.open(law.link, '_blank')}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: '4px',
                                borderRadius: '4px',
                                color: '#007bff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                              title="View Passage"
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                style={{ color: '#007bff' }}
                              >
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                <circle cx="12" cy="12" r="3"></circle>
                              </svg>
                            </button>
                          ) : (
                            <span style={{ color: '#6c757d', fontSize: '12px' }}>No link</span>
                          )}

                          {/* Delete icon */}
                          <button
                            onClick={() => {
                              const updatedLaws = selectedLaws.filter(l => l.id !== law.id);
                              setSelectedLaws(updatedLaws);
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '4px',
                              borderRadius: '4px',
                              color: '#dc3545',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                            title="Delete Law"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      }

      {
        isXAILoading && (
          <div style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            padding: '8px 16px',
            backgroundColor: '#fff',
            borderRadius: '4px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            zIndex: 1000
          }}>
            Loading XAI details...
          </div>
        )
      }

      {/* Fair Patterns Popup */}
      {
        isFairPatternsPopupOpen && selectedPointForFairPatterns && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1000,
            }}
            onClick={() => {
              setIsFairPatternsPopupOpen(false);
              setSelectedPointForFairPatterns(null);
            }}
          >
            <div
              style={{
                backgroundColor: 'white',
                padding: '20px',
                borderRadius: '8px',
                width: '60%',
                maxHeight: '80vh',
                overflow: 'auto'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px'
              }}>
                <h3>Fair Pattern Preview</h3>
                <button
                  onClick={() => {
                    setIsFairPatternsPopupOpen(false);
                    setSelectedPointForFairPatterns(null);
                  }}
                  style={{
                    backgroundColor: "transparent",
                    border: "1px solid #000EAB",
                    color: "#000EAB",
                    padding: "8px 24px",
                    borderRadius: "20px",
                    cursor: "pointer"
                  }}
                >
                  Close
                </button>
              </div>

              {selectedPointForFairPatterns.dark_pattern && (
                <div style={{ marginBottom: '20px' }}>
                  <h4>Parent Type: {selectedPointForFairPatterns.dark_pattern}</h4>
                </div>
              )}

              {/* Reference Fair Pattern Image */}
              <div style={{ marginBottom: '20px' }}>
                <h5 style={{ marginBottom: '12px', color: '#333', fontSize: '16px' }}>Reference Fair Object</h5>
                {selectedPointForFairPatterns.fairpatterns_url ? (
                  <div style={{ textAlign: 'center', border: '1px solid #ddd', borderRadius: '8px', padding: '10px' }}>
                    <img
                      src={selectedPointForFairPatterns.fairpatterns_url}
                      alt="Fair Pattern"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '60vh',
                        objectFit: 'contain',
                        borderRadius: '8px'
                      }}
                    />
                  </div>
                ) : (
                  <div style={{
                    textAlign: 'center',
                    padding: '20px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px'
                  }}>
                    <p>Fetch laws to get the fair pattern image</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      }

      {/* Recommendations Popup */}
      {/* Representative Fair Image Popup */}
      {
        isRepresentativePopupOpen && selectedNode && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1000,
            }}
            onClick={() => setIsRepresentativePopupOpen(false)}
          >
            <div
              style={{
                backgroundColor: 'white',
                padding: '30px',
                borderRadius: '12px',
                width: '70%',
                maxHeight: '85vh',
                overflow: 'auto',
                position: 'relative'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px'
              }}>
                <h3 style={{ margin: 0 }}>Representative Fair Image</h3>
                <button
                  onClick={() => setIsRepresentativePopupOpen(false)}
                  style={{
                    backgroundColor: "transparent",
                    border: "1px solid #000EAB",
                    color: "#000EAB",
                    padding: "8px 24px",
                    borderRadius: "20px",
                    cursor: "pointer"
                  }}
                >
                  Close
                </button>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}>

                {/* Image Display Area */}
                <div style={{
                  width: '100%',
                  minHeight: '300px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  border: '1px solid #dee2e6',
                  overflow: 'hidden'
                }}>
                  {selectedNode.representative_fair_pattern_url ? (
                    <img
                      src={selectedNode.representative_fair_pattern_url}
                      alt="Representative Fair Pattern"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '60vh',
                        objectFit: 'contain'
                      }}
                    />
                  ) : (
                    <div style={{ textAlign: 'center', color: '#6c757d' }}>
                      <p style={{ margin: 0 }}>No representative image generated yet.</p>
                    </div>
                  )}
                </div>

                {/* Action Area */}
                <div style={{ textAlign: 'center' }}>
                  {!selectedNode.representative_fair_pattern_url && (
                    <button
                      onClick={async () => {
                        setIsGeneratingRepresentative(true);
                        try {
                          // 1. Gather data from all points
                          const allIssues = points.map(p => p.text).filter(Boolean);
                          const allRecommendations = points.flatMap(p =>
                            Array.isArray(p.recommendations) ? p.recommendations : [p.recommendations]
                          ).filter(Boolean);

                          // Helper function to extract fair pattern URL from various possible locations
                          const extractFairPatternUrl = (point) => {
                            // First check if fairpatterns_url exists directly
                            if (point.fairpatterns_url) {
                              return point.fairpatterns_url;
                            }

                            // Check in fair_patterns.top_5_dark_patterns for fair_pattern_image_url
                            if (point.fair_patterns && point.fair_patterns.top_5_dark_patterns) {
                              for (const darkPattern of point.fair_patterns.top_5_dark_patterns) {
                                if (darkPattern.fair_pattern_image_url) {
                                  return darkPattern.fair_pattern_image_url;
                                }
                              }
                            }

                            // Check in fair_pattern_images array
                            if (point.fair_pattern_images && point.fair_pattern_images.length > 0) {
                              const firstImage = point.fair_pattern_images[0];
                              if (firstImage.url) {
                                return firstImage.url;
                              }
                            }

                            return null;
                          };

                          const allFairPatternUrls = points.map(p => extractFairPatternUrl(p)).filter(Boolean);

                          if (allIssues.length === 0) {
                            toast.warn("No issues found to generate image from.");
                            setIsGeneratingRepresentative(false);
                            return;
                          }

                          // 2. Call API
                          const response = await fetch(
                            `${process.env.REACT_APP_API_BASE_URL}/generate_fair_pattern_image`,
                            {
                              method: "POST",
                              headers: { "Content-Type": "application/json" },
                              body: JSON.stringify({
                                source_image_url: selectedNode.imageUrl,
                                reference_image_urls: allFairPatternUrls, // Sending array
                                recommendations: allRecommendations,      // Sending array
                                issues: allIssues,                        // Sending array
                                session_name: sessionName || sessionId
                              }),
                            }
                          );

                          if (!response.ok) {
                            throw new Error(`HTTP error! status: ${response.status}`);
                          }

                          const data = await response.json();

                          if (data.status === 'success' && data.image_url) {
                            // 3. Save to Firebase (including metadata)
                            const journeysRef = collection(db, "sessions", sessionId, "userJourneys");
                            const journeysSnapshot = await getDocs(journeysRef);
                            let updated = false;

                            for (const journeyDoc of journeysSnapshot.docs) {
                              const imagesRef = collection(journeyDoc.ref, "images");
                              const imagesSnapshot = await getDocs(imagesRef);

                              for (const imageDoc of imagesSnapshot.docs) {
                                if (imageDoc.data().imageUrl === selectedNode.imageUrl) {
                                  await updateDoc(imageDoc.ref, {
                                    representative_fair_pattern_url: data.image_url,
                                    representative_fair_pattern_metadata: data.metadata || null
                                  });
                                  updated = true;
                                  break;
                                }
                              }
                              if (updated) break;
                            }

                            // 4. Update Local State
                            setSelectedNode(prev => ({
                              ...prev,
                              representative_fair_pattern_url: data.image_url,
                              representative_fair_pattern_metadata: data.metadata || null
                            }));

                            toast.success("Representative fair image generated!");
                          } else {
                            throw new Error(data.message || 'Generation failed');
                          }

                        } catch (error) {
                          console.error("Error generating representative image:", error);
                          toast.error(`Failed to generate: ${error.message}`);
                        } finally {
                          setIsGeneratingRepresentative(false);
                        }
                      }}
                      disabled={isGeneratingRepresentative}
                      style={{
                        backgroundColor: "#000EAB",
                        color: "white",
                        border: "none",
                        padding: "12px 30px",
                        borderRadius: "30px",
                        fontSize: "16px",
                        cursor: isGeneratingRepresentative ? "not-allowed" : "pointer",
                        opacity: isGeneratingRepresentative ? 0.7 : 1,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '10px',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                      }}
                    >
                      {isGeneratingRepresentative ? (
                        <>
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                          Generating Image...
                        </>
                      ) : (
                        <>
                          <span>✨</span> Generate Representative Fair Image
                        </>
                      )}
                    </button>
                  )}

                  {/* Validation Information */}
                  {selectedNode.representative_fair_pattern_metadata && selectedNode.representative_fair_pattern_url && (
                    <div style={{
                      width: '100%',
                      marginTop: '20px',
                      padding: '20px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '8px',
                      border: '1px solid #dee2e6'
                    }}>
                      <h4 style={{ marginBottom: '16px', color: '#333', fontSize: '18px', fontWeight: '600' }}>Validation Results</h4>

                      {/* Dark Pattern Resolution */}
                      {selectedNode.representative_fair_pattern_metadata.validations?.dark_pattern_resolution && (
                        <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: 'white', borderRadius: '6px' }}>
                          <h5 style={{ fontSize: '14px', fontWeight: '600', color: '#000EAB', marginBottom: '8px' }}>Dark Pattern Resolution</h5>
                          <p style={{ margin: '4px 0', fontSize: '13px' }}><strong>Issue:</strong> {selectedNode.representative_fair_pattern_metadata.validations.dark_pattern_resolution.issue}</p>
                          <p style={{ margin: '4px 0', fontSize: '13px' }}><strong>Status:</strong> <span style={{
                            color: selectedNode.representative_fair_pattern_metadata.validations.dark_pattern_resolution.resolution_extent === 'resolved' ? '#00b74a' : '#ff6b6b',
                            fontWeight: '600'
                          }}>{selectedNode.representative_fair_pattern_metadata.validations.dark_pattern_resolution.resolution_extent}</span></p>
                          <p style={{ margin: '4px 0', fontSize: '13px', color: '#666' }}>{selectedNode.representative_fair_pattern_metadata.validations.dark_pattern_resolution.reasoning}</p>
                        </div>
                      )}

                      {/* Image Difference */}
                      {selectedNode.representative_fair_pattern_metadata.validations?.image_difference && (
                        <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: 'white', borderRadius: '6px' }}>
                          <h5 style={{ fontSize: '14px', fontWeight: '600', color: '#000EAB', marginBottom: '8px' }}>Image Difference Analysis</h5>
                          <p style={{ margin: '4px 0', fontSize: '13px' }}><strong>Difference:</strong> {selectedNode.representative_fair_pattern_metadata.validations.image_difference.percentage_difference}%</p>
                          <p style={{ margin: '4px 0', fontSize: '13px', color: '#666' }}>{selectedNode.representative_fair_pattern_metadata.validations.image_difference.reasoning}</p>
                        </div>
                      )}

                      {/* Recommendation Checks */}
                      {selectedNode.representative_fair_pattern_metadata.validations?.recommendation_check?.validations && (
                        <div style={{ padding: '12px', backgroundColor: 'white', borderRadius: '6px' }}>
                          <h5 style={{ fontSize: '14px', fontWeight: '600', color: '#000EAB', marginBottom: '12px' }}>Recommendation Validation</h5>
                          {selectedNode.representative_fair_pattern_metadata.validations.recommendation_check.validations.map((rec, idx) => (
                            <div key={idx} style={{
                              marginBottom: '12px',
                              paddingBottom: '12px',
                              borderBottom: idx < selectedNode.representative_fair_pattern_metadata.validations.recommendation_check.validations.length - 1 ? '1px solid #e9ecef' : 'none'
                            }}>
                              <p style={{ margin: '0 0 6px 0', fontSize: '13px', fontWeight: '500' }}>{rec.recommendation}</p>
                              <p style={{ margin: '4px 0', fontSize: '12px' }}>
                                <strong>Status:</strong> <span style={{
                                  color: rec.status === 'considered' ? '#00b74a' : rec.status === 'partial' ? '#ffa726' : '#999',
                                  fontWeight: '600',
                                  textTransform: 'capitalize'
                                }}>{rec.status.replace('_', ' ')}</span>
                              </p>
                              <p style={{ margin: '4px 0', fontSize: '12px', color: '#666' }}>{rec.reasoning}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {selectedNode.representative_fair_pattern_url && (
                    <button
                      onClick={async () => {
                        if (!window.confirm("Are you sure you want to regenerate? This will overwrite the existing image.")) return;
                        // Logic duplication for regeneration - reusing the same handler would be cleaner but inline is fine for now
                        // Triggering the same logic (copy-paste of onclick above, essentially)
                        setIsGeneratingRepresentative(true);
                        try {
                          const allIssues = points.map(p => p.text).filter(Boolean);
                          const allRecommendations = points.flatMap(p =>
                            Array.isArray(p.recommendations) ? p.recommendations : [p.recommendations]
                          ).filter(Boolean);

                          // Helper function to extract fair pattern URL from various possible locations
                          const extractFairPatternUrl = (point) => {
                            // First check if fairpatterns_url exists directly
                            if (point.fairpatterns_url) {
                              return point.fairpatterns_url;
                            }

                            // Check in fair_patterns.top_5_dark_patterns for fair_pattern_image_url
                            if (point.fair_patterns && point.fair_patterns.top_5_dark_patterns) {
                              for (const darkPattern of point.fair_patterns.top_5_dark_patterns) {
                                if (darkPattern.fair_pattern_image_url) {
                                  return darkPattern.fair_pattern_image_url;
                                }
                              }
                            }

                            // Check in fair_pattern_images array
                            if (point.fair_pattern_images && point.fair_pattern_images.length > 0) {
                              const firstImage = point.fair_pattern_images[0];
                              if (firstImage.url) {
                                return firstImage.url;
                              }
                            }

                            return null;
                          };

                          const allFairPatternUrls = points.map(p => extractFairPatternUrl(p)).filter(Boolean);

                          const response = await fetch(
                            `${process.env.REACT_APP_API_BASE_URL}/generate_fair_pattern_image`,
                            {
                              method: "POST",
                              headers: { "Content-Type": "application/json" },
                              body: JSON.stringify({
                                source_image_url: selectedNode.imageUrl,
                                reference_image_urls: allFairPatternUrls,
                                recommendations: allRecommendations,
                                issues: allIssues,
                                session_name: sessionName || sessionId
                              }),
                            }
                          );

                          if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
                          const data = await response.json();

                          if (data.status === 'success' && data.image_url) {
                            const journeysRef = collection(db, "sessions", sessionId, "userJourneys");
                            const journeysSnapshot = await getDocs(journeysRef);
                            let updated = false;

                            for (const journeyDoc of journeysSnapshot.docs) {
                              const imagesRef = collection(journeyDoc.ref, "images");
                              const imagesSnapshot = await getDocs(imagesRef);

                              for (const imageDoc of imagesSnapshot.docs) {
                                if (imageDoc.data().imageUrl === selectedNode.imageUrl) {
                                  await updateDoc(imageDoc.ref, {
                                    representative_fair_pattern_url: data.image_url,
                                    representative_fair_pattern_metadata: data.metadata || null
                                  });
                                  updated = true;
                                  break;
                                }
                              }
                              if (updated) break;
                            }

                            setSelectedNode(prev => ({
                              ...prev,
                              representative_fair_pattern_url: data.image_url,
                              representative_fair_pattern_metadata: data.metadata || null
                            }));
                            toast.success("Regenerated successfully!");
                          } else {
                            throw new Error(data.message || 'Generation failed');
                          }
                        } catch (error) {
                          toast.error(`Regeneration failed: ${error.message}`);
                        } finally {
                          setIsGeneratingRepresentative(false);
                        }
                      }}
                      style={{
                        backgroundColor: "white",
                        color: "#000EAB",
                        border: "1px solid #000EAB",
                        padding: "10px 24px",
                        borderRadius: "24px",
                        fontSize: "14px",
                        cursor: "pointer",
                        marginTop: '10px'
                      }}
                    >
                      🔄 Regenerate
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )
      }
      {/* Recommendations Popup */}
      {
        isRecommendationsPopupOpen && selectedPointForRecommendations && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1000,
            }}
            onClick={() => {
              setIsRecommendationsPopupOpen(false);
              setSelectedPointForRecommendations(null);
            }}
          >
            <div
              style={{
                backgroundColor: 'white',
                padding: '30px',
                borderRadius: '12px',
                width: '70%',
                maxWidth: '900px',
                maxHeight: '80vh',
                overflow: 'auto',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '24px',
                borderBottom: '2px solid #E8EAFF',
                paddingBottom: '16px'
              }}>
                <div>
                  <h2 style={{ margin: '0 0 8px 0', fontSize: '24px', fontWeight: '600', color: '#333' }}>
                    💡 Recommendations
                  </h2>
                  {selectedPointForRecommendations.dark_pattern && (
                    <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
                      For dark pattern: <strong>{selectedPointForRecommendations.dark_pattern}</strong>
                    </p>
                  )}
                </div>
                <button
                  onClick={() => {
                    setIsRecommendationsPopupOpen(false);
                    setSelectedPointForRecommendations(null);
                  }}
                  style={{
                    backgroundColor: "transparent",
                    border: "1px solid #000EAB",
                    color: "#000EAB",
                    padding: "10px 28px",
                    borderRadius: "24px",
                    cursor: "pointer",
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = '#000EAB';
                    e.target.style.color = '#fff';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = '#000EAB';
                  }}
                >
                  Close
                </button>
              </div>

              {selectedPointForRecommendations.recommendations &&
                selectedPointForRecommendations.recommendations.length > 0 ? (
                <div>
                  <div style={{
                    marginBottom: '20px',
                    padding: '16px',
                    backgroundColor: '#F0F7FF',
                    borderRadius: '8px',
                    borderLeft: '4px solid #000EAB'
                  }}>
                    <p style={{ margin: 0, fontSize: '14px', color: '#555', lineHeight: '1.6' }}>
                      {selectedPointForRecommendations.text}
                    </p>
                    {selectedPointForRecommendations.dark_pattern && (
                      <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#666' }}>
                        <strong>Dark Pattern:</strong> {selectedPointForRecommendations.dark_pattern}
                      </p>
                    )}
                    {selectedPointForRecommendations.type && (
                      <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#666' }}>
                        <strong>Type:</strong> {selectedPointForRecommendations.type}
                      </p>
                    )}
                  </div>

                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#333',
                    marginBottom: '16px'
                  }}>
                    Suggested Improvements:
                  </h3>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {selectedPointForRecommendations.recommendations.map((recommendation, index) => (
                      <div
                        key={index}
                        style={{
                          padding: '16px 20px',
                          backgroundColor: '#FAFBFF',
                          borderRadius: '8px',
                          border: '1px solid #E8EAFF',
                          display: 'flex',
                          gap: '12px',
                          alignItems: 'flex-start',
                          transition: 'all 0.2s',
                          cursor: 'default'
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.backgroundColor = '#F0F7FF';
                          e.currentTarget.style.borderColor = '#000EAB';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.backgroundColor = '#FAFBFF';
                          e.currentTarget.style.borderColor = '#E8EAFF';
                        }}
                      >
                        <div style={{
                          minWidth: '28px',
                          height: '28px',
                          borderRadius: '50%',
                          backgroundColor: '#000EAB',
                          color: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '14px',
                          fontWeight: '600',
                          flexShrink: 0
                        }}>
                          {index + 1}
                        </div>
                        <p style={{
                          margin: 0,
                          fontSize: '14px',
                          lineHeight: '1.6',
                          color: '#333',
                          flex: 1
                        }}>
                          {recommendation}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: '40px 20px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px'
                }}>
                  <p style={{ margin: 0, color: '#666', fontSize: '16px' }}>
                    No recommendations available for this point.
                  </p>
                  <p style={{ margin: '8px 0 0 0', color: '#999', fontSize: '14px' }}>
                    Please select a dark pattern to see recommendations.
                  </p>
                </div>
              )}
            </div>
          </div>
        )
      }

      {/* Add ToastContainer for notifications */}
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />

      {/* Modal for fullscreen image */}
      {
        isModalOpen && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0, 0, 0, 0.8)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1000,
            }}
            onClick={() => setIsModalOpen(false)}
          >
            <img
              src={selectedNode?.imageUrl}
              alt="Fullscreen Screenshot"
              style={{
                maxWidth: "90%",
                maxHeight: "90%",
                borderRadius: "10px",
              }}
            />
          </div>
        )
      }

      {/* Image Properties & Journey Popup */}
      {isPropertiesPopupOpen && selectedNode && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1100,
        }} onClick={() => setIsPropertiesPopupOpen(false)}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            width: '95%',
            height: '90%',
            maxWidth: '1440px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }} onClick={e => e.stopPropagation()}>
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid #f0f0f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: '#fff',
              zIndex: 10
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#1a1a1a' }}>
                  Image Properties
                </h3>
                <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#666' }} title={selectedNode.url || selectedNode.Url}>
                  {(() => {
                    const fullUrl = selectedNode.url || selectedNode.Url || "No URL";
                    return fullUrl.length > 70 ? fullUrl.substring(0, 70) + '...' : fullUrl;
                  })()}
                </p>
              </div>
              <button
                onClick={() => setIsPropertiesPopupOpen(false)}
                style={{
                  background: '#f5f5f5',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '36px',
                  height: '36px',
                  borderRadius: '10px',
                  transition: 'all 0.2s'
                }}
                onMouseOver={e => e.currentTarget.style.background = '#eee'}
                onMouseOut={e => e.currentTarget.style.background = '#f5f5f5'}
              >
                &times;
              </button>
            </div>

            <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
              {/* Left Side: Properties */}
              <div style={{
                width: '40%',
                minWidth: '350px',
                borderRight: '1px solid #f0f0f0',
                padding: '24px',
                overflowY: 'auto',
                backgroundColor: '#fafafa'
              }}>
                <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#1a1a1a', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ width: '4px', height: '16px', backgroundColor: '#000EAB', borderRadius: '2px' }}></span>
                  Properties
                </h4>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div style={{ backgroundColor: 'white', padding: '16px', borderRadius: '12px', border: '1px solid #f0f0f0' }}>
                    <label style={{ display: 'block', fontWeight: '600', color: '#888', fontSize: '11px', textTransform: 'uppercase', marginBottom: '4px' }}>Journey Name</label>
                    <div style={{ fontSize: '14px', color: '#000EAB', fontWeight: '600' }}>
                      {selectedNode.journeyName || "None"}
                    </div>
                  </div>

                  <div style={{ backgroundColor: 'white', padding: '16px', borderRadius: '12px', border: '1px solid #f0f0f0' }}>
                    <label style={{ display: 'block', fontWeight: '600', color: '#888', fontSize: '11px', textTransform: 'uppercase', marginBottom: '4px' }}>Captured By</label>
                    <div style={{ fontSize: '14px', color: '#333', fontWeight: '500' }}>
                      {(() => {
                        const currentJourney = session?.userJourneys?.find(j => j.name === selectedNode.journeyName);
                        return currentJourney?.createdBy || session?.createdBy || 'None';
                      })()}
                    </div>
                  </div>

                  <div style={{ backgroundColor: 'white', padding: '16px', borderRadius: '12px', border: '1px solid #f0f0f0' }}>
                    <label style={{ display: 'block', fontWeight: '600', color: '#888', fontSize: '11px', textTransform: 'uppercase', marginBottom: '4px' }}>Page Name</label>
                    <div style={{ fontSize: '14px', color: '#333', fontWeight: '500' }}>
                      {selectedNode.comprehensive_result?.categories?.webpage_category || selectedNode.pageName || "None"}
                    </div>
                  </div>

                  <div style={{ backgroundColor: 'white', padding: '16px', borderRadius: '12px', border: '1px solid #f0f0f0' }}>
                    <label style={{ display: 'block', fontWeight: '600', color: '#888', fontSize: '11px', textTransform: 'uppercase', marginBottom: '4px' }}>Website Category</label>
                    <div style={{ fontSize: '14px', color: '#333', fontWeight: '500' }}>
                      {selectedNode.comprehensive_result?.categories?.website_category || selectedNode.website_category || "None"}
                    </div>
                  </div>

                  <div style={{ backgroundColor: 'white', padding: '16px', borderRadius: '12px', border: '1px solid #f0f0f0' }}>
                    <label style={{ display: 'block', fontWeight: '600', color: '#888', fontSize: '11px', textTransform: 'uppercase', marginBottom: '4px' }}>Captured At</label>
                    <div style={{ fontSize: '14px', color: '#333', fontWeight: '500' }}>
                      {(() => {
                        const capturedAt = selectedNode.createdAt || selectedNode.imageCreatedAt;
                        if (!capturedAt) return "None";
                        if (capturedAt.seconds) return new Date(capturedAt.seconds * 1000).toLocaleString();
                        if (capturedAt instanceof Date) return capturedAt.toLocaleString();
                        if (typeof capturedAt === 'string') return new Date(capturedAt).toLocaleString();
                        if (capturedAt.toDate) return capturedAt.toDate().toLocaleString();
                        return "None";
                      })()}
                    </div>
                  </div>

                  <div style={{ backgroundColor: 'white', padding: '16px', borderRadius: '12px', border: '1px solid #f0f0f0' }}>
                    <label style={{ display: 'block', fontWeight: '600', color: '#888', fontSize: '11px', textTransform: 'uppercase', marginBottom: '4px' }}>Image Hash</label>
                    <div style={{ fontSize: '12px', color: '#333', fontFamily: 'monospace', wordBreak: 'break-all', backgroundColor: '#f8f9fa', padding: '8px', borderRadius: '6px', border: '1px dashed #ddd' }}>
                      {selectedNode.image_hash || (selectedNode.imageUrl ? "H-" + Math.abs(selectedNode.imageUrl.split('').reduce((a, b) => { a = ((a << 5) - a) + b.charCodeAt(0); return a | 0 }, 0)).toString(16) : "None")}
                    </div>
                  </div>

                  <div style={{ backgroundColor: 'white', padding: '16px', borderRadius: '12px', border: '1px solid #f0f0f0' }}>
                    <label style={{ display: 'block', fontWeight: '600', color: '#888', fontSize: '11px', textTransform: 'uppercase', marginBottom: '4px' }}>Image Reference</label>
                    <div style={{ fontSize: '12px', color: '#000EAB', wordBreak: 'break-all' }}>
                      {selectedNode.imageUrl || selectedNode.image_url || "None"}
                    </div>
                  </div>

                  <div style={{ backgroundColor: 'white', padding: '16px', borderRadius: '12px', border: '1px solid #f0f0f0' }}>
                    <label style={{ display: 'block', fontWeight: '600', color: '#888', fontSize: '11px', textTransform: 'uppercase', marginBottom: '4px' }}>Encryption Type</label>
                    <div style={{ fontSize: '14px', color: '#333', fontWeight: '500' }}>
                      Google-managed (AES-256 encryption)
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Side: User Journey */}
              <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                backgroundColor: '#fff'
              }}>
                <div style={{ padding: '16px 24px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#1a1a1a', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ width: '4px', height: '16px', backgroundColor: '#059669', borderRadius: '2px' }}></span>
                    User Journey Flow
                  </h4>
                </div>

                <div style={{ flex: 1, position: 'relative' }}>
                  {(() => {
                    if (!selectedJourneyNavigation || selectedJourneyNavigation.length === 0) {
                      return (
                        <div style={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#94a3b8',
                          gap: '12px'
                        }}>
                          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M9 17l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          <p style={{ fontSize: '14px' }}>No navigation data found for this journey</p>
                        </div>
                      );
                    }

                    // Find where the image corresponds in the flow
                    let captureIndex = selectedJourneyNavigation.findIndex(nav =>
                      (nav.url && (selectedNode.url === nav.url || selectedNode.Url === nav.url)) ||
                      (nav.to && (selectedNode.url === nav.to || selectedNode.Url === nav.to)) ||
                      (nav.from && (selectedNode.url === nav.from || selectedNode.Url === nav.from))
                    );

                    // Provide a default if no match was found (show from the beginning)
                    if (captureIndex === -1) captureIndex = Math.min(2, selectedJourneyNavigation.length - 1);

                    const startIndex = Math.max(0, captureIndex - 2);
                    const endIndex = Math.min(selectedJourneyNavigation.length, captureIndex + 3);

                    const visibleNavigation = selectedJourneyNavigation.slice(startIndex, endIndex);

                    return (
                      <ReactFlow
                        nodes={visibleNavigation.map((nav, relativeIndex) => {
                          const originalIndex = startIndex + relativeIndex;
                          const isImageCapturePoint = originalIndex === captureIndex;

                          let label = '';
                          if (nav.action) label = `Action: ${nav.action}`;
                          else if (nav.from && nav.to) label = `From: ${nav.from}\nTo: ${nav.to}`;
                          else if (nav.url) label = `URL: ${nav.url}`;
                          else label = `Step ${originalIndex + 1}`;

                          if (nav.timestamp) {
                            try {
                              const date = new Date(nav.timestamp);
                              label += `\nTime: ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
                            } catch (e) { }
                          }

                          const nodeLabel = (
                            <div style={{ textAlign: 'left', fontSize: '11px', width: '100%', wordBreak: 'break-word' }}>
                              {label.split('\n').map((line, i) => (
                                <div key={i} style={{ marginBottom: '2px', fontWeight: line.startsWith('Action') ? '700' : '400' }}>{line}</div>
                              ))}
                            </div>
                          );

                          return {
                            id: `nav-node-${originalIndex}`,
                            data: { label: nodeLabel },
                            position: { x: 50, y: relativeIndex * 120 + 20 },
                            style: {
                              width: 380,
                              padding: '12px',
                              borderRadius: '10px',
                              border: isImageCapturePoint ? '2px solid #000EAB' : '1px solid #e0e0e0',
                              backgroundColor: isImageCapturePoint ? '#EEF2FF' : '#fff',
                              boxShadow: isImageCapturePoint ? '0 4px 12px rgba(0, 14, 171, 0.15)' : '0 2px 4px rgba(0,0,0,0.05)',
                            },
                          };
                        })}
                        edges={visibleNavigation.slice(0, -1).map((_, relativeIndex) => {
                          const originalIndex = startIndex + relativeIndex;
                          return {
                            id: `nav-edge-${originalIndex}`,
                            source: `nav-node-${originalIndex}`,
                            target: `nav-node-${originalIndex + 1}`,
                            type: 'smoothstep',
                            style: { stroke: '#CBD5E1', strokeWidth: 2 },
                            markerEnd: { type: 'arrowclosed', color: '#CBD5E1' },
                          }
                        })}
                        fitView
                        style={{ height: '100%', outline: 'none' }}
                        nodesConnectable={false}
                        nodesDraggable={false}
                        elementsSelectable={false}
                        proOptions={{ hideAttribution: true }}
                      >
                        <Background color="#f1f5f9" gap={20} />
                        <Controls showInteractive={false} style={{ bottom: 20, right: 20 }} />
                      </ReactFlow>
                    );
                  })()}
                </div>
              </div>
            </div>

            <div style={{
              padding: '16px 24px',
              borderTop: '1px solid #f0f0f0',
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              backgroundColor: '#fff',
              zIndex: 10,
              gap: '12px'
            }}>
              <button
                onClick={() => handleExportProperties()}
                style={{
                  backgroundColor: 'white',
                  color: '#1a1a1a',
                  border: '1px solid #e0e0e0',
                  padding: '10px 20px',
                  borderRadius: '10px',
                  fontWeight: '600',
                  fontSize: '14px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s'
                }}
                onMouseOver={e => {
                  e.currentTarget.style.backgroundColor = '#f8f9fa';
                  e.currentTarget.style.borderColor = '#ccc';
                }}
                onMouseOut={e => {
                  e.currentTarget.style.backgroundColor = 'white';
                  e.currentTarget.style.borderColor = '#e0e0e0';
                }}
              >
                🖨️ Export PDF
              </button>

              <button
                onClick={() => setIsPropertiesPopupOpen(false)}
                style={{
                  backgroundColor: '#000EAB',
                  color: 'white',
                  border: 'none',
                  padding: '10px 32px',
                  borderRadius: '10px',
                  fontWeight: '600',
                  fontSize: '14px',
                  cursor: 'pointer',
                  boxShadow: '0 4px 6px -1px rgba(0, 14, 171, 0.2)',
                  transition: 'all 0.2s'
                }}
                onMouseOver={e => e.currentTarget.style.backgroundColor = '#000880'}
                onMouseOut={e => e.currentTarget.style.backgroundColor = '#000EAB'}
              >
                Close View
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Full-Screen Processing Overlay for PDF Export */}
      {isExportPDFLoading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backdropFilter: 'blur(2px)'
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '30px 40px',
            borderRadius: '16px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
          }}>
            <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem', marginBottom: '16px', color: '#000EAB' }}>
              <span className="visually-hidden">Loading...</span>
            </div>
            <h4 style={{ margin: 0, color: '#1a1a1a', fontWeight: '600' }}>Processing PDF...</h4>
            <p style={{ margin: '8px 0 0 0', color: '#666', fontSize: '14px' }}>Please wait while your report is being generated.</p>
          </div>
        </div>
      )}

    </div>
  );
};

export default NewEditor;

